import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { getChalets, reorderChalets,  addChalet,  deleteChalet,  updateChalet,  generateChaletDescription,  getAllBookings, sendBookingConfirmation, resendBookingConfirmation, getExtras,  addExtra, deleteExtra, updateExtra, addCoupon, deleteCoupon, getCoupons, updateBookingStatus, listUsers, createUser, updateUser, setUserChalets, getBookingNote, saveBookingNote, getExtraBeddengoed, saveExtraBeddengoed, getAdminBlogPosts, getBlogPost, createBlogPost, updateBlogPost, deleteBlogPost } from '../services/dataService';
import { StatusBadge } from "../services/statusBadge";
import { Reviews } from "./Reviews";
import {
  Chalet,
  UnavailablePeriod,
  CustomPricePeriod,
  DayPrices,
  Booking,
  ExtraOption,
  Coupon,
  ChaletExtra,
  BlogPost,
} from '../types';

import { AMENITIES } from '../services/mockData';

import { Plus, Trash2, Wand2, Loader2, Edit2, CalendarX, X, Zap, Upload, Link2, Image as ImageIcon, Users, User, Coins, Bell, Home, CalendarCheck, CheckCircle, Settings, Coffee, Languages, CreditCard, Save, Filter, Clock, XCircle, RefreshCw, AlertTriangle, Code, Copy, Check, Calendar, Download, Mail, Phone, MapPin, ExternalLink, ChevronLeft, ChevronRight, Send, RotateCcw, ShieldCheck, Lock, AlertCircle, LogOut, Inbox, History, Search, Ticket, Tag, Star, MessageSquare, ChevronDown, ChevronUp, BedDouble, UserPlus, BookOpen, FileText, Eye, EyeOff, Globe } from 'lucide-react';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';



import { format, parseISO, addDays, isAfter, isBefore, startOfDay } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import { jsPDF } from 'jspdf';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}


type BookingStatus =
  | 'pending'
  | 'pending_payment'
  | 'confirmed'
  | 'cancelled'
  | 'payment_failed'
  | 'payment_expired';

type BookingStatusResponse = {
  ok: boolean;
  id: number | string;
  status?: BookingStatus;
};


const PRESET_CATEGORIES = ['Bungalow', 'Chalet', 'Natuurhuisje', 'Wellness huisje'] as const;


type SortableImageTileProps = {
  id: string;
  url: string;
  onRemove: () => void;
};

const SortableImageTile: React.FC<SortableImageTileProps> = ({ id, url, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square rounded-2xl overflow-hidden shadow-sm border border-brand-100 group bg-white"
    >
      <img src={url} className="w-full h-full object-cover" alt="Property" />

      {/* Drag handle */}
      <button
        type="button"
        className="absolute top-2 left-2 z-10 rounded-full bg-white/90 hover:bg-white shadow px-2 py-1 text-xs font-bold cursor-grab active:cursor-grabbing"
        title="Sleep om te verplaatsen"
        aria-label="Sleep om te verplaatsen"
        {...attributes}
        {...listeners}
      >
        ≡
      </button>

      {/* Remove */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          type="button"
          onClick={onRemove}
          className="p-2 bg-white text-red-500 rounded-full shadow-lg transform hover:scale-110 transition-transform"
          aria-label="Verwijder foto"
          title="Verwijderen"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export const Admin: React.FC = () => {
  const { t } = useLanguage();
  

const AUTH_KEY = 'chalet_inn_admin_auth';
const [isAuthenticated, setIsAuthenticated] = useState(false);
const [userRole, setUserRole] = useState<string | null>(null);
const bookingsOnlyRole = userRole === 'owner' || userRole === 'cleaner';
const isAdmin = userRole === 'admin';
const setAuth = (value: boolean) => {
  setIsAuthenticated(value);
  try {
    if (value) localStorage.setItem(AUTH_KEY, 'true');
    else localStorage.removeItem(AUTH_KEY); // or setItem(AUTH_KEY, 'false')
  } catch {}
};

useEffect(() => {
  try {
    setIsAuthenticated(localStorage.getItem(AUTH_KEY) === 'true');
  } catch {
    setIsAuthenticated(false);
  }
}, []);

// IMPORTANT: any effects that use setActiveTab / setIsFormOpen must be declared
// AFTER the corresponding useState hooks to avoid TDZ runtime errors.

  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  // Data
  const [chalets, setChalets] = useState<Chalet[]>([]);

  // Chalet ordering (drag & drop)
  const [draggingChaletId, setDraggingChaletId] = useState<string | null>(null);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const moveChalet = useCallback((fromId: string, toId: string) => {
    setChalets((prev) => {
      const fromIndex = prev.findIndex((c) => String(c.id) === String(fromId));
      const toIndex = prev.findIndex((c) => String(c.id) === String(toId));
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setHasOrderChanges(true);
  }, []);

  const saveChaletOrder = useCallback(async () => {
    setIsSavingOrder(true);
    try {
      const orderedIds = chalets.map((c) => c.id);
      const ok = await reorderChalets(orderedIds);
      if (ok) {
        setHasOrderChanges(false);
      } else {
        alert("Opslaan van de volgorde is mislukt.");
      }
    } finally {
      setIsSavingOrder(false);
    }
  }, [chalets]);

  const [bookings, setBookings] = useState<Booking[]>([]);


  // Users (admin manage)
  type AdminUser = {
    id: number;
    username: string;
    role: 'admin' | 'owner' | 'cleaner';
    active: number; // 1/0
    created_at?: string;
    chaletIds: number[];
  };

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const [umUsername, setUmUsername] = useState('');
  const [umPassword, setUmPassword] = useState('');
  const [umEmail, setUmEmail] = useState('');
  const [umRole, setUmRole] = useState<'admin' | 'owner' | 'cleaner'>('owner');
  const [umActive, setUmActive] = useState(true);
  const [umChaletIds, setUmChaletIds] = useState<number[]>([]);
  const [usersSavedMsg, setUsersSavedMsg] = useState<string>('');
  // Modal states
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Booking note (internal staff notes)
  const [bookingNote, setBookingNote] = useState('');
  const [bookingNoteOriginal, setBookingNoteOriginal] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  // notes map: bookingId -> note text (for list view indicator)
  const [bookingNotesMap, setBookingNotesMap] = useState<Record<string, string>>({});

  // Extra beddengoed toggle (admin/owner settable)
  const [extraBeddengoed, setExtraBeddengoed] = useState(false);
  const [beddengoedSaving, setBeddengoedSaving] = useState(false);

  // Extras selected for currently viewed booking (loaded per chalet)
  const [selectedChaletExtras, setSelectedChaletExtras] = useState<ExtraOption[]>([]);


  // Booking payment/status badge (retrieved via bookings/getstatus.php?id=...)
  const [bookingStatus, setBookingStatus] = useState<BookingStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

useEffect(() => {
  if (!selectedBooking?.chaletId) {
    setSelectedChaletExtras([]);
    return;
  }

  (async () => {
    try {
      const ex = await getExtras(selectedBooking.chaletId); // IMPORTANT: per chalet
      setSelectedChaletExtras(ex);
    } catch {
      setSelectedChaletExtras([]);
    }
  })();
}, [selectedBooking?.chaletId]);

// Load note and extra beddengoed when a booking is opened
useEffect(() => {
  if (!selectedBooking) {
    setBookingNote('');
    setBookingNoteOriginal('');
    setExtraBeddengoed(false);
    return;
  }
  setNoteLoading(true);
  getBookingNote(selectedBooking.id).then((n) => {
    setBookingNote(n);
    setBookingNoteOriginal(n);
    setNoteLoading(false);
    // also update the map so list view indicator stays in sync
    setBookingNotesMap((prev) => ({ ...prev, [selectedBooking.id]: n }));
  }).catch(() => setNoteLoading(false));
  // Load extra beddengoed (fallback to value already on the booking object)
  getExtraBeddengoed(selectedBooking.id).then((v) => setExtraBeddengoed(v)).catch(() => {
    setExtraBeddengoed(!!selectedBooking.extraBeddengoed);
  });
}, [selectedBooking?.id]);

// Prefetch notes for all bookings (for list view indicator)
useEffect(() => {
  if (!bookings.length) return;
  bookings.forEach((b) => {
    getBookingNote(b.id).then((n) => {
      if (n) {
        setBookingNotesMap((prev) => ({ ...prev, [b.id]: n }));
      }
    }).catch(() => {});
  });
}, [bookings]);

// Begin Media upload chalet
const [newImagesStr, setNewImagesStr] = useState('');
const fileInputRef = useRef<HTMLInputElement>(null);
const [showUrlInput, setShowUrlInput] = useState(false);
const [tempUrl, setTempUrl] = useState('');

const images = useMemo(() => {
  return newImagesStr
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}, [newImagesStr]);

const setImages = (imgs: string[]) => setNewImagesStr(imgs.join('\n'));

const sensors = useSensors(
  useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
);

const handleImagesDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;
  if (!over) return;
  if (active.id === over.id) return;

  const oldIndex = images.indexOf(String(active.id));
  const newIndex = images.indexOf(String(over.id));
  if (oldIndex === -1 || newIndex === -1) return;

  setImages(arrayMove(images, oldIndex, newIndex));
};

const handleRemoveImage = (idx: number) => {
  setImages(images.filter((_, i) => i !== idx));
};

const handleAddUrl = () => {
  const url = tempUrl.trim();
  if (!url) return;

  if (images.includes(url)) {
    setTempUrl('');
    setShowUrlInput(false);
    return;
  }

  setImages([...images, url]);
  setTempUrl('');
  setShowUrlInput(false);
};


const handleFileUpload: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
	const finalImages = newImagesStr
	  .split('\n')
	  .map((s) => s.trim())
	  .filter(Boolean);

  const file = e.target.files?.[0];
  if (!file) return;

  try {
    const fd = new FormData();
    fd.append('file', file);

    const res = await fetch('/api/upload.php', {
      method: 'POST',
      body: fd,
      credentials: 'include',
    });

    const j = await res.json().catch(() => ({}));
    if (!res.ok || !j?.ok || !j?.url) {
      throw new Error(j?.error || `Upload failed (HTTP ${res.status})`);
    }

    setNewImagesStr(prev => {
      const lines = prev.split('\n').map(s => s.trim()).filter(Boolean);
      lines.push(String(j.url));
      return lines.join('\n');
    });
  } catch (err: any) {
    alert(err?.message ?? 'Upload failed');
  } finally {
    e.target.value = '';
  }
};



// End Media upload chalet

  const badge = useMemo(() => {
    if (statusLoading) {
      return {
        className: 'bg-stone-100 text-stone-600',
        icon: <Clock size={12} />,
        label: 'Status ophalen…',
      };
    }

    if (statusError) {
      return {
        className: 'bg-red-100 text-red-700',
        icon: <AlertTriangle size={12} />,
        label: 'Status onbekend',
      };
    }

    switch (bookingStatus) {
      case 'confirmed':
        return {
          className: 'bg-green-100 text-green-700',
          icon: <ShieldCheck size={12} />,
          label: 'Bevestigd',
        };

      case 'pending_payment':
        return {
          className: 'bg-amber-100 text-amber-800',
          icon: <Clock size={12} />,
          label: 'Wachten op betaling',
        };

      case 'pending':
        return {
          className: 'bg-amber-100 text-amber-800',
          icon: <Clock size={12} />,
          label: 'In behandeling',
        };

      case 'payment_failed':
        return {
          className: 'bg-red-100 text-red-700',
          icon: <XCircle size={12} />,
          label: 'Betaling mislukt',
        };

      case 'payment_expired':
        return {
          className: 'bg-red-100 text-red-700',
          icon: <XCircle size={12} />,
          label: 'Betaallink verlopen',
        };

      case 'cancelled':
        return {
          className: 'bg-stone-200 text-stone-700',
          icon: <XCircle size={12} />,
          label: 'Geannuleerd',
        };

      default:
        return {
          className: 'bg-stone-100 text-stone-600',
          icon: <Clock size={12} />,
          label: 'Status onbekend',
        };
    }
  }, [bookingStatus, statusLoading, statusError]);


  const [searchQuery, setSearchQuery] = useState('');
  const [extraOptions, setExtraOptions] = useState<ExtraOption[]>([]);
  const [formExtras, setFormExtras] = useState<ExtraOption[]>([]); // extras for currently edited chalet
  const [copied, setCopied] = useState(false);
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeSavedMsg, setStripeSavedMsg] = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState<'chalets' | 'bookings' | 'coupons' | 'reviews' | 'manage' | 'blog'>('chalets');
  const [isFormOpen, setIsFormOpen] = useState(false);

  // --- Blog state ---
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogPage, setBlogPage] = useState(1);
  const [blogPages, setBlogPages] = useState(1);
  const [blogTotal, setBlogTotal] = useState(0);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogFormOpen, setBlogFormOpen] = useState(false);
  const [blogEditingPost, setBlogEditingPost] = useState<BlogPost | null>(null);
  const [blogTitle, setBlogTitle] = useState('');
  const [blogSlug, setBlogSlug] = useState('');
  const [blogExcerpt, setBlogExcerpt] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogAuthor, setBlogAuthor] = useState('Chalet Inn');
  const [blogCoverImage, setBlogCoverImage] = useState('');
  const [blogImages, setBlogImages] = useState<string[]>([]);
  const [blogPublished, setBlogPublished] = useState(false);
  const [blogSaving, setBlogSaving] = useState(false);
  const [blogUploadingCover, setBlogUploadingCover] = useState(false);
  const [blogUploadingImage, setBlogUploadingImage] = useState(false);
  const [blogDeleteConfirm, setBlogDeleteConfirm] = useState<string | null>(null);

  const resetBlogForm = () => {
    setBlogEditingPost(null);
    setBlogTitle('');
    setBlogSlug('');
    setBlogExcerpt('');
    setBlogContent('');
    setBlogAuthor('Chalet Inn');
    setBlogCoverImage('');
    setBlogImages([]);
    setBlogPublished(false);
  };

  const loadBlogPosts = useCallback(async (p = 1) => {
    setBlogLoading(true);
    try {
      const res = await getAdminBlogPosts(p);
      setBlogPosts(res.posts ?? []);
      setBlogPages(res.pages ?? 1);
      setBlogTotal(res.total ?? 0);
      setBlogPage(p);
    } catch {
      // ignore
    } finally {
      setBlogLoading(false);
    }
  }, []);

  const openBlogCreate = () => {
    resetBlogForm();
    setBlogFormOpen(true);
  };

  const openBlogEdit = async (post: BlogPost) => {
    setBlogFormOpen(true);
    setBlogLoading(true);
    try {
      const full = await getBlogPost(String(post.id));
      const p = full ?? post;
      setBlogEditingPost(p);
      setBlogTitle(p.title ?? '');
      setBlogSlug(p.slug ?? '');
      setBlogExcerpt(p.excerpt ?? '');
      setBlogContent(p.content ?? '');
      setBlogAuthor(p.author ?? 'Chalet Inn');
      setBlogCoverImage(p.cover_image ?? '');
      setBlogImages(p.images ?? []);
      setBlogPublished(p.published ?? false);
    } finally {
      setBlogLoading(false);
    }
  };

  const handleBlogSave = async () => {
    if (!blogTitle.trim() || !blogContent.trim()) return;
    setBlogSaving(true);
    try {
      if (blogEditingPost) {
        await updateBlogPost({
          id: blogEditingPost.id,
          title: blogTitle,
          slug: blogSlug,
          excerpt: blogExcerpt,
          content: blogContent,
          author: blogAuthor,
          cover_image: blogCoverImage,
          images: blogImages,
          published: blogPublished,
        });
      } else {
        await createBlogPost({
          title: blogTitle,
          slug: blogSlug,
          excerpt: blogExcerpt,
          content: blogContent,
          author: blogAuthor,
          cover_image: blogCoverImage,
          images: blogImages,
          published: blogPublished,
        });
      }
      setBlogFormOpen(false);
      resetBlogForm();
      loadBlogPosts(blogPage);
    } catch {
      alert('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setBlogSaving(false);
    }
  };

  const handleBlogDelete = async (id: string) => {
    try {
      await deleteBlogPost(id);
      setBlogDeleteConfirm(null);
      loadBlogPosts(blogPage);
    } catch {
      alert('Verwijderen mislukt.');
    }
  };

  const uploadBlogImage = async (file: File, isCover: boolean) => {
    const formData = new FormData();
    formData.append('file', file);
    if (isCover) setBlogUploadingCover(true);
    else setBlogUploadingImage(true);
    try {
      const res = await fetch('api/upload.php', { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        if (isCover) setBlogCoverImage(data.url);
        else setBlogImages(prev => [...prev, data.url]);
      }
    } catch {
      alert('Upload mislukt.');
    } finally {
      if (isCover) setBlogUploadingCover(false);
      else setBlogUploadingImage(false);
    }
  };

  // --- Admin users tab helpers ---
  const resetUserModal = useCallback(() => {
    setEditingUser(null);
    setUmUsername('');
    setUmPassword('');
    setUmEmail('');
    setUmRole('owner');
    setUmActive(true);
    setUmChaletIds([]);
    setUsersSavedMsg('');
    setUsersError(null);
  }, []);

  const openCreateUser = useCallback(() => {
    resetUserModal();
    setUserModalOpen(true);
  }, [resetUserModal]);

  const openEditUser = useCallback((u: AdminUser) => {
    setEditingUser(u);
    setUmUsername(u.username);
    setUmPassword('');
    setUmEmail(u.email ?? '');
    setUmRole(u.role);
    setUmActive(!!u.active);
    setUmChaletIds(Array.isArray(u.chaletIds) ? u.chaletIds : []);
    setUsersSavedMsg('');
    setUsersError(null);
    setUserModalOpen(true);
  }, []);

  const refreshUsers = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const list = await listUsers();
      setUsers(list || []);
    } catch (e: any) {
      setUsersError(e?.message || 'Kon gebruikers niet laden.');
    } finally {
      setUsersLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    if (activeTab !== 'manage') return;
    if (!isAuthenticated || !isAdmin) return;
    refreshUsers();
  }, [activeTab, isAuthenticated, isAdmin, refreshUsers]);

  const saveUser = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;

    setUsersError(null);
    setUsersSavedMsg('');

    try {
      if (!editingUser) {
        if (!umUsername.trim()) throw new Error('Gebruikersnaam is verplicht.');
        if (!umPassword) throw new Error('Wachtwoord is verplicht.');
        await createUser({
          username: umUsername.trim(),
          password: umPassword,
          role: umRole,
          active: umActive ? 1 : 0,
          email: umEmail.trim() || null,
        });
        setUsersSavedMsg('Gebruiker aangemaakt.');
      } else {
        await updateUser({
          id: editingUser.id,
          role: umRole,
          active: umActive ? 1 : 0,
          password: umPassword ? umPassword : undefined,
          email: umEmail.trim() || null,
        });
        setUsersSavedMsg('Gebruiker bijgewerkt.');
      }

      // Chalet access (for owner/cleaner typically, but admin can have it too)
      const userId = editingUser?.id;
      if (userId) {
        await setUserChalets(userId, umChaletIds);
      } else {
        // after create: reload and find created user by username then set chalets
        await refreshUsers();
        const created = users.find((u) => u.username === umUsername.trim());
        if (created) {
          await setUserChalets(created.id, umChaletIds);
        }
      }

      await refreshUsers();
    } catch (e: any) {
      setUsersError(e?.message || 'Opslaan mislukt.');
      return;
    }
  }, [
    isAuthenticated,
    isAdmin,
    editingUser,
    umUsername,
    umPassword,
    umEmail,
    umRole,
    umActive,
    umChaletIds,
    refreshUsers,
    users,
  ]);


  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  // NOTE: Effects that use setActiveTab / setIsFormOpen must be declared AFTER those hooks
  // to avoid "Cannot access <var> before initialization" (TDZ) issues in production builds.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!bookingsOnlyRole) return;
    // Force restricted users onto the bookings tab
    setActiveTab('bookings');
    setIsFormOpen(false);
  }, [isAuthenticated, bookingsOnlyRole]);

  useEffect(() => {
    // Non-admins must never land on the Manage tab
    if (!isAuthenticated) return;
    if (isAdmin) return;
    if (activeTab !== 'manage') return;
    setActiveTab(bookingsOnlyRole ? 'bookings' : 'chalets');
  }, [isAuthenticated, isAdmin, bookingsOnlyRole, activeTab]);

  // Bookings Sub-Tabs
  const [bookingsSubTab, setBookingsSubTab] = useState<'active' | 'archive'>('active');
  const [bookingsPage, setBookingsPage] = useState(1);
  const BOOKINGS_PER_PAGE = 10;

  // New Coupon Form
  const [newCoupon, setNewCoupon] = useState<{
      code: string;
      discountType: 'percentage' | 'fixed';
      discountValue: number;
      expirationDate: string;
  }>({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      expirationDate: format(addDays(new Date(), 30), 'yyyy-MM-dd')
  });

  // Bookings filter
  const [bookingStatusFilter, setBookingStatusFilter] = useState<'all' | 'confirmed' | 'pending' | 'pending_payment' | 'cancelled'>('all');
  const [selectedApartmentFilter, setSelectedApartmentFilter] = useState<string>('all');

  // Processing & notifications
  const [isProcessing, setIsProcessing] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Form: basic
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPrice, setNewPrice] = useState(100);
  const [newCleaningFee, setNewCleaningFee] = useState(45);
  const [newReservationCost, setNewReservationCost] = useState(20);
  const [newSmoobuIcalUrl, setNewSmoobuIcalUrl] = useState('');
  const [newTouristTax, setNewTouristTax] = useState(20);
  const [newLocation, setNewLocation] = useState('');
  const [newProvince, setNewProvince] = useState<string>('');
  const [isCustomProvince, setIsCustomProvince] = useState(false);
  const [newCat, setNewCat] = useState<string>('Natuurhuisje');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [newIsActive, setNewIsActive] = useState(true);

  const [newAmenities, setNewAmenities] = useState<string[]>([]);
  const [customAmenityInput, setCustomAmenityInput] = useState('');

  const [newMaxGuests, setNewMaxGuests] = useState(4);
  
  const [newMinNights, setNewMinNights] = useState<number>(1);
  const [newMinStayPeriods, setNewMinStayPeriods] = useState<
    { startDate: string; endDate: string; minNights: number }[]
  >([]);

  // Inputs to add a new min-stay period row
  const [minStayStart, setMinStayStart] = useState('');
  const [minStayEnd, setMinStayEnd] = useState('');
  const [minStayMinNights, setMinStayMinNights] = useState<number>(7);

  // Advanced: availability + custom prices
  const [newUnavailablePeriods, setNewUnavailablePeriods] = useState<UnavailablePeriod[]>([]);
  const [newCustomPrices, setNewCustomPrices] = useState<CustomPricePeriod[]>([]);

  // Day prices
  const [dayPrices, setDayPrices] = useState<DayPrices>({
    monday: 100,
    tuesday: 100,
    wednesday: 100,
    thursday: 100,
    friday: 100,
    saturday: 100,
    sunday: 100,
  });
const PRESET_PROVINCES = [
  'Groningen','Friesland','Drenthe','Overijssel','Flevoland','Gelderland',
  'Utrecht','Noord-Holland','Zuid-Holland','Zeeland','Noord-Brabant','Limburg',
] as const;  

  // Local inputs for blocks & prices
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [priceStart, setPriceStart] = useState('');
  const [priceEnd, setPriceEnd] = useState('');
  const [customPriceVal, setCustomPriceVal] = useState(100);

  // Extras UI
  const [newExtraLabel, setNewExtraLabel] = useState('');
  const [newExtraPrice, setNewExtraPrice] = useState(0);
  const [editingExtraId, setEditingExtraId] = useState<string | null>(null);
  // Chalet Extras (per chalet)
  const [chaletExtras, setChaletExtras] = useState<any[]>([]);
  const [originalExtras, setOriginalExtras] = useState<any[]>([]);

  const addExtraRow = () => {
    // Temp row; will be created in DB on Save
    setChaletExtras((prev) => [
      ...prev,
      { id: `tmp_${Date.now()}`, label: "", price: 0, priceType: "one-time" as const },
    ]);
  };

  const updateChaletExtra = (id: string, key: "label" | "price" | "priceType", value: any) => {
    setChaletExtras((prev) =>
      prev.map((ex) => (ex.id === id ? { ...ex, [key]: value } : ex))
    );
  };

  const removeExtra = (id: string) => {
    setChaletExtras((prev) => prev.filter((ex) => ex.id !== id));
  };											  

   const addNotification = useCallback((message: string, type: Notification['type'] = 'info') => {
    const id = Math.random().toString(36).substring(7);
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  const refresh = async () => {
	const [c, b] = await Promise.all([getChalets(true), getAllBookings()]);
	setChalets(c);
	setBookings(b);
  };
  
  

	useEffect(() => {
	  (async () => {
		const c = await getChalets(true);
		
		setChalets(c);
		setExtraOptions([]);

		if (isAuthenticated) {
		  const b = await getAllBookings();
		  setBookings(b);
		}
	  })();
	}, [isAuthenticated]);

useEffect(() => {
  if (!isAuthenticated) return;
  if (activeTab !== 'coupons') return;

  (async () => {
    const data = await getCoupons();
    setCoupons(data);
  })();
}, [isAuthenticated, activeTab]);

useEffect(() => {
  if (!isAuthenticated || !isAdmin) return;
  if (activeTab !== 'blog') return;
  loadBlogPosts(1);
}, [isAuthenticated, isAdmin, activeTab, loadBlogPosts]);

useEffect(() => {
  (async () => {
    try {
      const res = await fetch('/api/auth/me.php', { credentials: 'include' });
      const j = await res.json();
      setAuth(!!j?.authenticated);
      // Expect role from backend (e.g. { role: 'admin'|'owner'|'cleaner' })
      setUserRole(j?.role ?? j?.user?.role ?? null);
    } catch {
      setAuth(false);
    }
  })();
}, []);
 
const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoginError('');

  try {
    const res = await fetch('/api/auth/login.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // <-- IMPORTANT (send/receive session cookie)
	  body: JSON.stringify({ username: loginUser, password: loginPass }),
    });

    const j = await res.json().catch(() => ({}));

    if (!res.ok || !j?.success) {
      setAuth(false);
      setUserRole(null);
      setLoginError(j?.error || 'Inloggen mislukt.');
      return;
    }

    setAuth(true);
    setUserRole(j?.role ?? j?.user?.role ?? null);
  } catch {
    setAuth(false);
    setUserRole(null);
    setLoginError('Netwerkfout bij inloggen.');
  }
};

const activeBookingsCount = bookings.filter((b) => {
  if (b.status === "cancelled") return false;

  const chalet = chalets.find(c => String(c.id) === String(b.chaletId));
  if ((chalet as any)?.isActive === 0) return false;

  const end = parseISO(b.endDate.replace(" ", "T"));
  return !isBefore(startOfDay(end), startOfDay(new Date()));
}).length;

const handleLogout = async () => {
  try {
    await fetch('/api/auth/logout.php', {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    setAuth(false);
    setUserRole(null);
  }
};


const handleSaveNote = async () => {
  if (!selectedBooking) return;
  setNoteSaving(true);
  const ok = await saveBookingNote(selectedBooking.id, bookingNote);
  if (ok) {
    setBookingNoteOriginal(bookingNote);
    setBookingNotesMap((prev) => ({ ...prev, [selectedBooking.id]: bookingNote }));
    addNotification('Notitie opgeslagen.', 'success');
  } else {
    addNotification('Kon notitie niet opslaan.', 'error');
  }
  setNoteSaving(false);
};

const handleToggleBeddengoed = async (newValue: boolean) => {
  if (!selectedBooking) return;
  setExtraBeddengoed(newValue); // optimistic local state
  // Also update the bookings list immediately so the overview reflects it right away
  setBookings((prev) =>
    prev.map((b) => b.id === selectedBooking.id ? { ...b, extraBeddengoed: newValue } : b)
  );
  setBeddengoedSaving(true);
  const ok = await saveExtraBeddengoed(selectedBooking.id, newValue);
  if (!ok) {
    // Rollback both
    setExtraBeddengoed(!newValue);
    setBookings((prev) =>
      prev.map((b) => b.id === selectedBooking.id ? { ...b, extraBeddengoed: !newValue } : b)
    );
    addNotification('Kon beddengoed status niet opslaan.', 'error');
  }
  setBeddengoedSaving(false);
};

const handleCancelBooking = async (id: string) => {
  if (isProcessing) return;               // prevents double clicks
  setIsProcessing(true);                  // disables button immediately

	try {
		const success = await updateBookingStatus(id, 'cancelled');
		if (success) {
			// Update both list and selected detail view
			setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
			if (selectedBooking && selectedBooking.id === id) {
				setSelectedBooking(prev => prev ? { ...prev, status: 'cancelled' } : null);
			}
			addNotification('Boeking succesvol geannuleerd.', 'success');
		} else {
			addNotification('Kon boeking niet annuleren.', 'error');
		}
	} catch (error) {
		addNotification('Fout bij het annuleren van de boeking.', 'error');
	} finally {
		setIsProcessing(false);
	}
};

  
const handleResendConfirmation = async (booking: Booking) => {
  setIsProcessing(true);
  try {
    const ok = await resendBookingConfirmation(booking.id);

    if (!ok) {
      addNotification("Fout bij het verzenden van de mail.", "error");
      return;
    }

    addNotification(
      `Bevestigingsmail voor ${booking.guestName} is opnieuw verzonden.`,
      "success"
    );
  } catch {
    addNotification("Fout bij het verzenden van de mail.", "error");
  } finally {
    setIsProcessing(false);
  }
};


  const resetForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setNewTitle('');
    setNewDesc('');
    setNewPrice(100);
    setNewCleaningFee(45);
    setNewReservationCost(0);
    setNewTouristTax(0);
    setNewSmoobuIcalUrl('');
    setNewLocation('');
    setNewProvince('');
    setIsCustomProvince(false);
    setNewCat('Natuurhuisje');
    setIsCustomCategory(false);
    setNewAmenities([]);
    setNewIsActive(true);
    setCustomAmenityInput('');
    setNewMaxGuests(4);
    setNewMinNights(1);
    setNewMinStayPeriods([]);
    setMinStayStart('');
    setMinStayEnd('');
    setMinStayMinNights(7);
    setNewImagesStr('');
    setNewUnavailablePeriods([]);
    setNewCustomPrices([]);
    setDayPrices({ monday: 100, tuesday: 100, wednesday: 100, thursday: 100, friday: 100, saturday: 100, sunday: 100 });
    setBlockStart('');
    setBlockEnd('');
    setPriceStart('');
    setPriceEnd('');
    setCustomPriceVal(100);
    setDayPrices({
      monday: 100,
      tuesday: 100,
      wednesday: 100,
      thursday: 100,
      friday: 100,
      saturday: 100,
      sunday: 100,
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Huisje verwijderen uit database?')) {
      const success = await deleteChalet(id);
      if (success) refresh();
      else alert('Fout bij verwijderen.');
    }
  };

  const handleEdit = async (chalet: Chalet) => {
    setIsEditing(true);
    setEditingId(chalet.id);

    setNewTitle(chalet.title);
    setNewDesc(chalet.description);
    setNewPrice(chalet.pricePerNight);
    setNewCleaningFee(chalet.cleaningFee || 45);
    setNewReservationCost(chalet.reservationCost  || 0);
    setNewTouristTax(chalet.touristTax  || 0);
    setNewSmoobuIcalUrl(((chalet as any).smoobuIcalUrl ?? (chalet as any).smoobu_ical_url ?? '') as string);
    setNewLocation(chalet.location);
    setNewProvince(chalet.province || '');
    setIsCustomProvince(!PRESET_PROVINCES.includes((chalet.province || '') as any));

    setNewCat(chalet.category);
    setIsCustomCategory(!PRESET_CATEGORIES.includes(chalet.category as any));
    setNewIsActive((chalet as any).isActive !== undefined ? !!(chalet as any).isActive : true);

    setNewAmenities(chalet.amenities || []);
    setNewMaxGuests(chalet.maxGuests);
    setNewMinNights(chalet.minNights ?? 1);
    setNewMinStayPeriods(chalet.minStayPeriods ?? []);
    setNewImagesStr(Array.isArray(chalet.images) ? chalet.images.join('\n') : '');

    setNewUnavailablePeriods(chalet.unavailablePeriods || []);
    setNewCustomPrices(chalet.customPricePeriods || []);

    // Important: if dayPrices missing/empty -> fallback to base price
    setDayPrices({
      monday: chalet.dayPrices?.monday ?? chalet.pricePerNight,
      tuesday: chalet.dayPrices?.tuesday ?? chalet.pricePerNight,
      wednesday: chalet.dayPrices?.wednesday ?? chalet.pricePerNight,
      thursday: chalet.dayPrices?.thursday ?? chalet.pricePerNight,
      friday: chalet.dayPrices?.friday ?? chalet.pricePerNight,
      saturday: chalet.dayPrices?.saturday ?? chalet.pricePerNight,
      sunday: chalet.dayPrices?.sunday ?? chalet.pricePerNight,
    });
	  const ex = await getExtras(chalet.id);

    setChaletExtras(ex as any);
    setOriginalExtras(ex as any);


	  setIsFormOpen(true);
	  window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Load extras for the chalet you're editing (extras are per chalet)
  useEffect(() => {
    if (!isFormOpen) return;

    // Only load extras when editing an existing chalet
    if (!editingId) {
      setFormExtras([]);
      return;
    }

    (async () => {
      const extras = await getExtras(editingId);
      setFormExtras(extras);
    })();
  }, [isFormOpen, editingId]);

  const handleAddExtraRow = async () => {
    if (!editingId) return;

        const ok = await addExtra({
      id: "",
      chaletId: editingId,
      label: "Nieuwe extra",
      price: 0,
      priceType: "one-time",
    } as any);

    if (ok) {
      const extras = await getExtras(editingId);
      setFormExtras(extras);
    } else {
      alert("Kon extra niet toevoegen.");
    }
  };

  const updateExtraRow = async (id: string, field: "label" | "price" | "priceType", value: any) => {
    if (!editingId) return;

    const existing = formExtras.find((e) => e.id === id);
    if (!existing) return;

    const updated: any = { ...existing, [field]: value, chalet_id: editingId };

    // optimistic UI
    setFormExtras((prev) => prev.map((e) => (e.id === id ? updated : e)));

    // persist
    await updateExtra(updated);
  };


  const toggleAmenity = (id: string) => {
    setNewAmenities((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAddCustomAmenity = () => {
    const v = customAmenityInput.trim();
    if (v && !newAmenities.includes(v)) {
      setNewAmenities((prev) => [...prev, v]);
      setCustomAmenityInput('');
    }
  };

  const updateDayPrice = (day: keyof DayPrices, val: number) => {
    setDayPrices((prev) => ({ ...prev, [day]: val }));
  };

  const handleAddBlock = () => {
    if (blockStart && blockEnd) {
      setNewUnavailablePeriods((prev) => [...prev, { startDate: blockStart, endDate: blockEnd }]);
      setBlockStart('');
      setBlockEnd('');
    }
  };

  const handleRemoveBlock = (idx: number) => {
    setNewUnavailablePeriods((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddCustomPrice = () => {
    if (priceStart && priceEnd && customPriceVal) {
      setNewCustomPrices((prev) => [...prev, { startDate: priceStart, endDate: priceEnd, price: customPriceVal }]);
      setPriceStart('');
      setPriceEnd('');
      setCustomPriceVal(100);
    }
  };

  const handleRemoveCustomPrice = (idx: number) => {
    setNewCustomPrices((prev) => prev.filter((_, i) => i !== idx));
  };
  
  const handleAddMinStayPeriod = () => {
    if (!minStayStart || !minStayEnd) return;

    const mn = Math.max(1, Number(minStayMinNights || 1));

    setNewMinStayPeriods((prev) => [
      ...prev,
      { startDate: minStayStart, endDate: minStayEnd, minNights: mn },
    ]);

    setMinStayStart('');
    setMinStayEnd('');
    setMinStayMinNights(7);
  };

  const handleRemoveMinStayPeriod = (idx: number) => {
    setNewMinStayPeriods((prev) => prev.filter((_, i) => i !== idx));
  };


  const handleGenerateAI = async () => {
    if (!newTitle) return alert('Vul eerst een titel in.');
    setLoadingAI(true);
    const desc = await generateChaletDescription(newTitle, newAmenities.join(', '));
    setNewDesc(desc);
    setLoadingAI(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

const finalImages = newImagesStr
  .split('\n')
  .map((s) => s.trim())
  .filter(Boolean);


    const fallbackImage = [`https://picsum.photos/seed/${newTitle.replace(/\s/g, '')}/800/600`];

    const commonData = {
      title: newTitle,
      description: newDesc,
      pricePerNight: newPrice, // base price
      cleaningFee: newCleaningFee,
      location: newLocation,
      category: newCat,
      province: (newProvince || '').trim() || 'Onbekend',
      images: finalImages.length ? finalImages : fallbackImage,
      maxGuests: newMaxGuests,
      minNights: newMinNights,
      minStayPeriods: newMinStayPeriods,
      amenities: newAmenities,
	  reservationCost: newReservationCost,
	  touristTax: newTouristTax,
      smoobuIcalUrl: (newSmoobuIcalUrl || '').trim(),
      unavailablePeriods: newUnavailablePeriods,
      customPricePeriods: newCustomPrices,
      dayPrices: dayPrices,
      isActive: newIsActive ? 1 : 0,
    };

    // Keep rating/reviews from existing row when updating
    const existing = editingId ? chalets.find((c) => c.id === editingId) : undefined;

    const payload: Chalet = {
      id: isEditing ? (editingId || '') : '', // IMPORTANT: no local IDs on create
      rating: existing?.rating ?? 0,
      reviews: existing?.reviews ?? [],
      ...commonData,
    };

    const success = isEditing ? await updateChalet(payload) : await addChalet(payload);

    if (success) {
      // ✅ Sync extras (per chalet) with the same Save button
      try {
        // Figure out which chalet id to use
        let chaletIdToUse: string | undefined = isEditing ? String(editingId) : undefined;

        // If it's a NEW chalet, we try to find it after creation (best-effort)
        if (!chaletIdToUse) {
          const latest = (await getChalets())
            .filter((c) => c.title === newTitle && c.location === newLocation)
            .slice(-1)[0];
          chaletIdToUse = latest?.id ? String(latest.id) : undefined;
        }

        if (chaletIdToUse) {
          const deleted = isEditing
            ? (originalExtras as any[]).filter((o) => !(chaletExtras as any[]).some((c) => c.id === o.id))
            : [];

          const created = (chaletExtras as any[]).filter((c) => String(c.id).startsWith("tmp_"));
          const updated = (chaletExtras as any[]).filter((c) => !String(c.id).startsWith("tmp_"));

          await Promise.all([
            ...deleted.map((d) => deleteExtra(String(d.id), chaletIdToUse!)),
            ...updated.map((u) =>
              updateExtra({
                id: String(u.id),
                chaletId: chaletIdToUse!,
                label: String(u.label ?? ""),
                price: Number(u.price ?? 0),
                priceType: (u.priceType ?? "one-time") as any,
              } as any)
            ),
            ...created.map((c) =>
              addExtra({
                id: "",
                chaletId: chaletIdToUse!,
                label: String(c.label ?? ""),
                price: Number(c.price ?? 0),
                priceType: (c.priceType ?? "one-time") as any,
              } as any)
            ),
          ]);

          // refresh snapshot
          setOriginalExtras(chaletExtras as any);
        }
      } catch (err) {
        console.error("Error saving extras for chalet:", err);
        addNotification("Kon extra's niet opslaan. Controleer je chalet_id + API.", "error");
      }
      setIsFormOpen(false);
      resetForm();
      refresh();
    } else {
      alert('Fout bij opslaan in database.');
    }
  };

  const countBookingsForChalet = (chaletId: string) => bookings.filter((b) => b.chaletId === chaletId).length;

  const filteredBookings = useMemo(() => {
    if (bookingStatusFilter === 'all') return bookings;
    return bookings.filter((b) => b.status === bookingStatusFilter);
  }, [bookings, bookingStatusFilter]);



  // Extras (OLD) (remove later)
  const handleSaveExtra = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newExtraLabel.trim() === '') return;

    const success = editingExtraId
      ? await updateExtra({ id: editingExtraId, label: newExtraLabel, price: newExtraPrice })
      : await addExtra({ id: '', label: newExtraLabel, price: newExtraPrice });

    if (success) {
      setNewExtraLabel('');
      setNewExtraPrice(0);
      setEditingExtraId(null);
      refresh();
    }
  };

  const handleEditExtra = (extra: ExtraOption) => {
    setEditingExtraId(extra.id);
    setNewExtraLabel(extra.label);
    setNewExtraPrice(extra.price);
  };

  const handleCancelEditExtra = () => {
    setEditingExtraId(null);
    setNewExtraLabel('');
    setNewExtraPrice(0);
  };

  const handleDeleteExtra = async (id: string) => {
    if (window.confirm('Verwijderen?')) {
      await deleteExtra(id);
      refresh();
    }
  };
  // END EXTRAS OLD (remove later)
  
  
   const downloadBookingPDF = (booking: Booking) => {
    const chalet = chalets.find(c => c.id === booking.chaletId);
    // --- HTML print / "Save as PDF" invoice (fallback to jsPDF if pop-up blocked) ---
    const guestName = booking.guestName ?? '';
    const guestEmail = (booking as any).guestEmail ?? '';
    const guestPhone = (booking as any).guestPhone ?? '';
    const chaletTitle = chalet?.title ?? 'Onbekend';
    const total = Number(booking.totalPrice ?? 0).toFixed(2);

    const html = `
      <html>
        <head>
          <title>Boeking ${booking.id}</title>
          <meta charset="utf-8" />
          <style>
            body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; padding: 24px; }
            h1 { margin: 0 0 12px; }
            .muted { color: #666; }
            .row { margin: 6px 0; }
            .box { margin-top: 18px; padding: 16px; border: 1px solid #ddd; border-radius: 12px; }
          </style>
        </head>
        <body>
          <h1>Boeking</h1>
          <div class="muted">ID: ${booking.id}</div>

          <div class="box">
            <div class="row"><strong>Gast:</strong> ${guestName}</div>
            <div class="row"><strong>E-mail:</strong> ${guestEmail}</div>
            <div class="row"><strong>Telefoon:</strong> ${guestPhone}</div>
            <div class="row"><strong>Accommodatie:</strong> ${chaletTitle}</div>
            <div class="row"><strong>Check-in:</strong> ${(booking as any).startDate ?? ''}</div>
            <div class="row"><strong>Check-out:</strong> ${(booking as any).endDate ?? ''}</div>
            <div class="row"><strong>Totaal:</strong> €${total}</div>
          </div>

          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
      return;
    }
    // If we get here, pop-up is blocked -> continue with jsPDF fallback
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(22);
    doc.setFont("serif", "bold");
    doc.text("Boeking Bevestiging", 20, 25);
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Boeking ID: ${booking.id}`, 20, 32);
    doc.text(`Gegenereerd op: ${new Date().toLocaleDateString()}`, 140, 25);
    
    // Horizontal Line
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 40, 190, 40);
    
    // Booking Details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text("Verblijf Informatie", 20, 50);
    doc.setFontSize(11);
    doc.text(`Accommodatie: ${chalet?.title || 'N/A'}`, 20, 58);
    doc.text(`Locatie: ${chalet?.location || 'N/A'}`, 20, 64);
    doc.text(`Check-in: ${format(parseISO(booking.startDate), 'd MMMM yyyy')}`, 20, 70);
    doc.text(`Check-out: ${format(parseISO(booking.endDate), 'd MMMM yyyy')}`, 20, 76);
    
    // Guest Information
    doc.setFontSize(14);
    doc.text("Gast Gegevens", 110, 50);
    doc.setFontSize(11);
    doc.text(`Naam: ${booking.guestName}`, 110, 58);
    doc.text(`E-mail: ${booking.guestEmail || 'N/A'}`, 110, 64);
    doc.text(`Telefoon: ${booking.guestPhone || 'N/A'}`, 110, 70);
    doc.text(`Adres: ${booking.guestAddress || 'N/A'}`, 110, 76);
    doc.text(`${booking.guestZip || ''} ${booking.guestCity || ''}`, 110, 82);
    
    // Payment info
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 95, 190, 95);
    doc.setFontSize(14);
    doc.text("Financieel Overzicht", 20, 105);
    doc.setFontSize(11);
    doc.text(`Totaal Bedrag:`, 20, 113);
    doc.setFontSize(14);
    doc.text(`EUR ${booking.totalPrice.toFixed(2)}`, 150, 113, { align: 'right' });
    
    if (booking.extras.length > 0) {
        doc.setFontSize(11);
        doc.text("Geselecteerde Extra's:", 20, 125);
		const opt = selectedChaletExtras.find(o => o.id === extra);
		doc.text(`- ${opt?.label || extra}`, 25, 131 + (i * 6));

    }

    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("Bedankt voor het boeken bij Chalet Inn.", 20, 270);
    
    doc.save(`boeking-${booking.id}.pdf`);
    addNotification('PDF Dossier gedownload.', 'info');
  };

const handleAddCoupon = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!newCoupon.code || newCoupon.discountValue <= 0) {
    addNotification("Vul alle coupon-velden correct in.", "error");
    return;
  }

  const coupon: Coupon = {
    // id is NOT needed here anymore; DB will create it
    id: "", // can be omitted if your type allows, otherwise keep empty string
    code: newCoupon.code.toUpperCase(),
    discountType: newCoupon.discountType,
    discountValue: newCoupon.discountValue,
    expirationDate: newCoupon.expirationDate,
  };

  const ok = await addCoupon(coupon);

  if (!ok) {
    addNotification("Coupon aanmaken mislukt.", "error");
    return;
  }

  // Reload from DB so you get real id + availableFrom
  const fresh = await getCoupons();
  setCoupons(fresh);

  setNewCoupon({
    code: "",
    discountType: "percentage",
    discountValue: 0,
    expirationDate: format(addDays(new Date(), 30), "yyyy-MM-dd"),
  });

  addNotification(`Coupon ${coupon.code} aangemaakt.`, "success");
};

const handleDeleteCoupon = async (id: string) => {
  if (!window.confirm("Verwijderen?")) return;

  const ok = await deleteCoupon(id);

  if (!ok) {
    addNotification("Coupon verwijderen mislukt.", "error");
    return;
  }

  const fresh = await getCoupons();
  setCoupons(fresh);

  addNotification("Coupon gedeactiveerd.", "info");	
};




  const generateRandomCode = () => {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      setNewCoupon(prev => ({ ...prev, code }));
  };

	// Split and Filter Logic
	const categorizedBookings = useMemo(() => {
	  const today = startOfDay(new Date());
	  
	  const active: Booking[] = [];
	  const archived: Booking[] = [];

	  bookings.forEach(b => {
		const chaletForBooking = chalets.find(c => String(c.id) === String(b.chaletId));
		if ((chaletForBooking as any)?.isActive === 0) return;

		const matchesStatus = bookingStatusFilter === 'all' || b.status === bookingStatusFilter;
		const matchesSearch = b.guestName.toLowerCase().includes(searchQuery.toLowerCase()) || b.id.toLowerCase().includes(searchQuery.toLowerCase());
		const matchesApartment = selectedApartmentFilter === 'all' || String(b.chaletId) === selectedApartmentFilter;

		if (matchesStatus && matchesSearch && matchesApartment) {
		  const end = parseISO(b.endDate.replace(' ', 'T'));
		  const isExpired = isBefore(startOfDay(end), today);
		  const shouldArchive = b.status === "cancelled" || isExpired;

		  if (shouldArchive) archived.push(b);
		  else active.push(b);
		}
	  });

	  // Sort both arrays by start date ascending
	  const byStartDate = (a: Booking, b: Booking) =>
		parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime();

	  active.sort(byStartDate);
	  archived.sort(byStartDate);

	  return { active, archived };
	}, [bookings, bookingStatusFilter, searchQuery, selectedApartmentFilter]);

	const visibleBookings = useMemo(() => {
	  return bookingsSubTab === 'archive'
		? categorizedBookings.archived
		: categorizedBookings.active;
	}, [bookingsSubTab, categorizedBookings]);

	const totalBookingsPages = Math.max(1, Math.ceil(visibleBookings.length / BOOKINGS_PER_PAGE));
	const pagedBookings = useMemo(() => {
	  const start = (bookingsPage - 1) * BOOKINGS_PER_PAGE;
	  return visibleBookings.slice(start, start + BOOKINGS_PER_PAGE);
	}, [visibleBookings, bookingsPage, BOOKINGS_PER_PAGE]);


  const checkIsExpired = (dateString?: string) => {
      if (!dateString) return false;
      return isAfter(startOfDay(new Date()), startOfDay(parseISO(dateString)));
  };
 
if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50 px-6 py-12">
        <div className="w-full max-w-md bg-white border border-brand-100 p-10 rounded-sm shadow-2xl animate-in fade-in zoom-in duration-500">
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-brand-900 rounded-sm flex items-center justify-center text-white font-serif font-bold text-3xl shadow-xl mb-6">C</div>
            <h1 className="text-2xl font-serif font-bold text-brand-900 tracking-tight">Admin Portal</h1>
            <p className="text-[10px] text-stone-400 font-black uppercase tracking-[0.2em] mt-2">Chalet Inn Management</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-4">
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <input 
                  type="text" 
                  placeholder="Gebruikersnaam" 
                  value={loginUser}
                  onChange={(e) => setLoginUser(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-brand-50 border border-brand-100 rounded-sm text-sm focus:ring-1 focus:ring-brand-900 outline-none transition-all"
                  required
                />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <input 
                  type="password" 
                  placeholder="Wachtwoord" 
                  value={loginPass}
                  onChange={(e) => setLoginPass(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-brand-50 border border-brand-100 rounded-sm text-sm focus:ring-1 focus:ring-brand-900 outline-none transition-all"
                  required
                />
              </div>
            </div>

            {loginError && (
              <div className="bg-red-50 text-red-600 p-4 text-xs font-bold rounded-sm border border-red-100 flex items-center gap-3">
                <AlertCircle size={14}/> {loginError}
              </div>
            )}

            <button 
              type="submit"
              className="w-full py-4 bg-brand-900 text-white text-[10px] font-black uppercase tracking-[0.3em] hover:bg-black transition-all shadow-lg active:scale-[0.98] rounded-sm"
            >
              Verifiëren en Inloggen
            </button>
          </form>

          <p className="mt-8 text-center text-[9px] text-stone-300 font-black uppercase tracking-widest">
            Alleen geautoriseerde toegang
          </p>
        </div>
      </div>
    );
  }
  


  return (
    <>

	{/* Notifications */}
	<div className="fixed top-24 right-6 z-[60] flex flex-col gap-3 pointer-events-none">
	{notifications.map(n => (
	  <div 
		key={n.id} 
		className={`pointer-events-auto px-6 py-4 rounded-xl shadow-2xl border flex items-center gap-4 min-w-[300px] animate-slide-in-right ${
		  n.type === 'success' ? 'bg-white border-green-100 text-green-900' : 
		  n.type === 'error' ? 'bg-white border-red-100 text-red-900' : 
		  'bg-white border-brand-100 text-brand-900'
		}`}
	  >
		{n.type === 'success' ? <CheckCircle className="text-green-500" size={20} /> :
		 n.type === 'error' ? <AlertCircle className="text-red-500" size={20} /> :
		 <Loader2 className="text-brand-500 animate-spin" size={20} />}
		<span className="text-sm font-bold">{n.message}</span>
	  </div>
	))}
	</div>
  
    {/* Header */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 animate-in fade-in duration-700">
        <div>
          <h1 className="text-3xl font-serif font-bold text-brand-900">Dashboard</h1>
          <p className="text-stone-500">Overzicht van aanbod en reserveringen</p>
        </div>
		
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 transition-all ml-4">
            <LogOut size={14}/> Uitloggen
          </button>

        {activeTab === 'chalets' && !bookingsOnlyRole && (
          <button
            onClick={() => {
              if (isFormOpen) resetForm();
              setIsFormOpen(!isFormOpen);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-brand-800 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-lg transition-all transform active:scale-95"
          >
            <Plus size={20} /> {isFormOpen ? 'Annuleren' : 'Nieuw Chalet'}
          </button>
        )}

        {activeTab === 'blog' && isAdmin && !blogFormOpen && (
          <button
            onClick={openBlogCreate}
            className="flex items-center gap-2 px-6 py-3 bg-brand-800 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-lg transform active:scale-95"
          >
            <Plus size={20} /> Nieuw bericht
          </button>
        )}
        {activeTab === 'blog' && isAdmin && blogFormOpen && (
          <button
            onClick={() => { setBlogFormOpen(false); resetBlogForm(); }}
            className="flex items-center gap-2 px-6 py-3 bg-white border border-brand-200 text-brand-800 rounded-xl hover:bg-brand-50 transition-colors shadow transform active:scale-95"
          >
            <X size={18} /> Annuleren
          </button>
        )}
      </div>

      {/* Tabs — dropdown on mobile, pill bar on desktop */}
      <div className="mb-8">
        {/* Mobile dropdown */}
        <div className="md:hidden">
          <select
            value={activeTab}
            onChange={(e) => {
              const tab = e.target.value as typeof activeTab;
              setActiveTab(tab);
              setIsFormOpen(false);
              if (tab !== 'blog') setBlogFormOpen(false);
            }}
            className="w-full px-4 py-3 bg-white border border-brand-100 rounded-2xl text-sm font-semibold text-brand-900 shadow-sm outline-none focus:ring-2 focus:ring-brand-200"
          >
            {!bookingsOnlyRole && <option value="chalets">Chalets</option>}
            <option value="bookings">Boekingen{activeBookingsCount > 0 ? ` (${activeBookingsCount})` : ''}</option>
            {!bookingsOnlyRole && <option value="coupons">Kortingscodes</option>}
            {!bookingsOnlyRole && <option value="reviews">Reviews</option>}
            {isAdmin && <option value="manage">Gebruikers</option>}
            {isAdmin && <option value="blog">Blog</option>}
          </select>
        </div>

        {/* Desktop pill bar */}
        <div className="hidden md:flex gap-1 bg-brand-100/50 p-1 rounded-2xl w-fit border border-brand-100">
          {!bookingsOnlyRole && (
            <button
              onClick={() => setActiveTab('chalets')}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'chalets' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
              }`}
            >
              <Home size={15} /> Chalets
            </button>
          )}

          <button
            onClick={() => { setActiveTab('bookings'); setIsFormOpen(false); }}
            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all relative whitespace-nowrap ${
              activeTab === 'bookings' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
            }`}
          >
            <Bell size={15} /> Boekingen
            {activeBookingsCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm animate-pulse">
                {activeBookingsCount}
              </span>
            )}
          </button>

          {!bookingsOnlyRole && (
            <button
              onClick={() => { setActiveTab('coupons'); setIsFormOpen(false); }}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'coupons' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
              }`}
            >
              <Ticket size={15} /> Kortingscodes
            </button>
          )}

          {!bookingsOnlyRole && (
            <button
              onClick={() => { setActiveTab('reviews'); setIsFormOpen(false); }}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'reviews' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
              }`}
            >
              <Star size={15} /> Reviews
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => { setActiveTab('manage'); setIsFormOpen(false); }}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'manage' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
              }`}
            >
              <Users size={15} /> Gebruikers
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => { setActiveTab('blog'); setIsFormOpen(false); setBlogFormOpen(false); }}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'blog' ? 'bg-white text-brand-900 shadow-sm' : 'text-stone-500 hover:text-brand-700'
              }`}
            >
              <BookOpen size={15} /> Blog
            </button>
          )}
        </div>
      </div>

      {/* Chalets Tab */}
      {activeTab === 'chalets' && !bookingsOnlyRole && (
        <>
          {isFormOpen && (
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-brand-100 mb-10 animate-in slide-in-from-top-4">
              <h2 className="text-xl font-bold text-brand-900 mb-6">{isEditing ? `Bewerk "${newTitle}"` : 'Nieuw object toevoegen'}</h2>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium mb-1">Naam</label>
                  <input
                    required
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full p-3 border rounded-xl"
                    placeholder="Bijv. Bosvilla Veluwe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Locatie</label>
                  <input required value={newLocation} onChange={(e) => setNewLocation(e.target.value)} className="w-full p-3 border rounded-xl" />
                </div>

                {/* Province */}
                <div>
                  <label className="block text-sm font-medium mb-1">Provincie</label>

                  {!isCustomProvince ? (
                    <select
                      value={newProvince}
                      onChange={(e) => {
                        if (e.target.value === 'CUSTOM_OPTION') {
                          setIsCustomProvince(true);
                          setNewProvince('');
                        } else {
                          setNewProvince(e.target.value);
                        }
                      }}
                      className="w-full p-3 border rounded-xl"
                      required
                    >
                      <option value="" disabled>
                        Kies provincie…
                      </option>
                      {PRESET_PROVINCES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                      <option value="CUSTOM_OPTION">Anders…</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={newProvince}
                        onChange={(e) => setNewProvince(e.target.value)}
                        className="w-full p-3 border rounded-xl"
                        placeholder="Typ provincie"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomProvince(false);
                          setNewProvince('');
                        }}
                        className="px-3 py-2 border rounded-xl text-sm font-medium"
                        title="Terug naar lijst"
                      >
                        Terug
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Basis Prijs (fallback)</label>
                    <input required type="number" value={newPrice} onChange={(e) => setNewPrice(Number(e.target.value))} className="w-full p-3 border rounded-xl" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Schoonmaakkosten</label>
                    <input required type="number" value={newCleaningFee} onChange={(e) => setNewCleaningFee(Number(e.target.value))} className="w-full p-3 border rounded-xl" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Reserveringskosten</label>
                      <input required type="number" value={newReservationCost} onChange={(e) => setNewReservationCost(Number(e.target.value))} className="w-full p-3 border rounded-xl" />
                    </div>

                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Toeristenbelasting</label>
                    <input required type="number" value={newTouristTax} onChange={(e) => setNewTouristTax(Number(e.target.value))} className="w-full p-3 border rounded-xl" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                    <Users size={14} /> Max. Gasten
                  </label>
                  <input required type="number" value={newMaxGuests} onChange={(e) => setNewMaxGuests(Number(e.target.value))} className="w-full p-3 border rounded-xl" />
                </div>
				
                  <div className="col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
					  <div>
						<label className="block text-sm font-medium mb-1">iCal import (Smoobu)</label>
						<input
						  type="url"
						  value={newSmoobuIcalUrl}
						  onChange={(e) => setNewSmoobuIcalUrl(e.target.value)}
						  className="w-full p-3 border rounded-xl"
						  placeholder="https://login.smoobu.com/ical/..."
						/>
					  </div>

					  <div>
						<label className="block text-sm font-medium mb-1">iCal export (ChaletInn)</label>
						<input
						  type="text"
						  disabled
						  value={`https://chaletinn.nl/api/bookings/ical.php?apartment_id=${editingId ? editingId : 'CURRENT APARTMENT ID'}`}
						  className="w-full p-3 border rounded-xl bg-stone-100 text-stone-500"
						/>
					  </div>
				   </div>
				</div>			
				
                {/* ✅ Minimum stay settings */}
                <div className="col-span-2 space-y-4">
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest ml-1">
                    Minimum verblijf
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Standaard miniminaal aantal nachten</label>
                      <input
                        type="number"
                        min={1}
                        value={newMinNights}
                        onChange={(e) => setNewMinNights(Math.max(1, Number(e.target.value || 1)))}
                        className="w-full p-3 border rounded-xl"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        Wordt gebruikt als er geen periode-regel geldt.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-brand-100 bg-brand-50 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">Vanaf</label>
                        <input
                          type="date"
                          value={minStayStart}
                          onChange={(e) => setMinStayStart(e.target.value)}
                          className="w-full p-3 border rounded-xl bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Tot</label>
                        <input
                          type="date"
                          value={minStayEnd}
                          onChange={(e) => setMinStayEnd(e.target.value)}
                          className="w-full p-3 border rounded-xl bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Minimaal aantal nachten</label>
                        <input
                          type="number"
                          min={1}
                          value={minStayMinNights}
                          onChange={(e) => setMinStayMinNights(Math.max(1, Number(e.target.value || 1)))}
                          className="w-full p-3 border rounded-xl bg-white"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleAddMinStayPeriod}
                        className="px-6 py-3 bg-brand-900 text-white rounded-xl font-semibold hover:opacity-95"
                      >
                        Toevoegen
                      </button>
                    </div>

                    {newMinStayPeriods.length === 0 ? (
                      <p className="text-sm text-stone-500 italic">Geen minimum verblijf periodes ingesteld.</p>
                    ) : (
                      <div className="space-y-2">
                        {newMinStayPeriods.map((p, idx) => (
                          <div
                            key={`${p.startDate}-${p.endDate}-${idx}`}
                            className="flex items-center justify-between gap-3 p-3 bg-white border border-brand-100 rounded-xl"
                          >
                            <div className="text-sm text-stone-700">
                              <span className="font-semibold">{p.startDate}</span> →{" "}
                              <span className="font-semibold">{p.endDate}</span>{" "}
                              <span className="ml-2 text-stone-500">({p.minNights} nachten)</span>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveMinStayPeriod(idx)}
                              className="p-2 text-red-500 hover:text-red-700"
                              title="Verwijderen"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-1">Categorie</label>
                  {!isCustomCategory ? (
                    <select
                      value={newCat}
                      onChange={(e) => {
                        if (e.target.value === 'CUSTOM_OPTION') {
                          setIsCustomCategory(true);
                          setNewCat('');
                        } else {
                          setNewCat(e.target.value);
                        }
                      }}
                      className="w-full p-3 border rounded-xl bg-white"
                    >
                      {PRESET_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input autoFocus value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Type nieuwe categorie..." className="w-full p-3 border rounded-xl" />
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomCategory(false);
                          setNewCat(PRESET_CATEGORIES[0]);
                        }}
                        className="px-3 bg-brand-100 text-brand-800 rounded-xl text-sm font-medium hover:bg-brand-200"
                      >
                        Annuleren
                      </button>
                    </div>
                  )}
                </div>
				
			  {/* Media Section */}
				<div className="col-span-2 space-y-4">
				  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest ml-1">
					Media Beheer
				  </label>

				  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
					<DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleImagesDragEnd}
                  >
                    <SortableContext items={images} strategy={rectSortingStrategy}>
                      {images.map((url) => (
                        <SortableImageTile
                          key={url}
                          id={url}
                          url={url}
                          onRemove={() => handleRemoveImage(images.indexOf(url))}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  <button
					  type="button"
					  onClick={() => fileInputRef.current?.click()}
					  className="aspect-square rounded-2xl border-2 border-dashed border-brand-100 flex flex-col items-center justify-center gap-2 text-stone-400 hover:border-brand-900 hover:text-brand-900 hover:bg-brand-50 transition-all group"
					>
					  <Upload size={24} className="group-hover:scale-110 transition-transform" />
					  <span className="text-[9px] font-black uppercase tracking-widest">Upload Foto</span>
					</button>

					<input
					  type="file"
					  ref={fileInputRef}
					  className="hidden"
					  accept="image/*"
					  onChange={handleFileUpload}
					/>

					<button
					  type="button"
					  onClick={() => setShowUrlInput((v) => !v)}
					  className="aspect-square rounded-2xl border-2 border-dashed border-brand-100 flex flex-col items-center justify-center gap-2 text-stone-400 hover:border-brand-900 hover:text-brand-900 hover:bg-brand-50 transition-all group"
					>
					  <Link2 size={24} className="group-hover:scale-110 transition-transform" />
					  <span className="text-[9px] font-black uppercase tracking-widest">Voeg URL toe</span>
					</button>
				  </div>

				  {showUrlInput && (
					<div className="flex gap-2 animate-in slide-in-from-top-2">
					  <input
						type="text"
						value={tempUrl}
						onChange={(e) => setTempUrl(e.target.value)}
						placeholder="Plak afbeeldings-URL hier..."
						className="flex-grow p-4 bg-brand-50 border border-brand-100 rounded-2xl text-xs focus:ring-1 focus:ring-brand-900 outline-none"
					  />
					  <button
						type="button"
						onClick={handleAddUrl}
						className="px-6 py-4 bg-brand-900 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-lg"
					  >
						Toevoegen
					  </button>
					</div>
				  )}
				</div>


                {/* Amenities */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Voorzieningen</label>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {AMENITIES.map((am) => (
                      <button
                        type="button"
                        key={am.id}
                        onClick={() => toggleAmenity(am.id)}
                        className={`px-3 py-1 rounded-lg text-sm border transition-colors ${
                          newAmenities.includes(am.id) ? 'bg-brand-100 border-brand-500 text-brand-900 font-medium' : 'bg-white text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        {am.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 p-3 bg-brand-50 rounded-xl border border-brand-100">
                    {newAmenities.length === 0 && <span className="text-sm text-stone-400 italic">Geen voorzieningen geselecteerd</span>}
                    {newAmenities.map((amId) => {
                      const standard = AMENITIES.find((a) => a.id === amId);
                      return (
                        <div key={amId} className="flex items-center gap-1 px-3 py-1 bg-white border border-brand-200 rounded-full text-xs font-medium text-brand-900 shadow-sm">
                          <span>{standard ? standard.label : amId}</span>
                          <button type="button" onClick={() => toggleAmenity(amId)} className="text-red-400 hover:text-red-600 ml-1">
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Description + AI */}
                <div className="col-span-2">
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium">Beschrijving</label>
                  </div>
                  <textarea required rows={4} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} className="w-full p-3 border rounded-xl" placeholder="Typ een omschrijving..." />
                </div>
				
				{/* Extras */}
                <div className="col-span-2 bg-brand-50 rounded-2xl p-6 border border-brand-100">
				  <div className="flex justify-between items-center mb-6">
					  <h3 className="font-bold text-brand-900 flex items-center gap-2">
						<Coffee size={18} /> Extra's
					  </h3>
					  <button type="button" onClick={addExtraRow} className="bg-brand-200 text-brand-800 px-4 py-2 rounded-lg font-medium hover:bg-brand-300 transition-colors">Toevoegen</button>
				  </div>
				  <table className="w-full text-left">
					  <thead className="bg-brand-50 font-black text-stone-400 tracking-widest border-b border-brand-100">
						  <tr>
							  <th className="px-6 py-4">Naam </th>
							  <th className="px-6 py-4">Tarief (€)</th>
							  <th className="px-6 py-4">Frequentie</th>
							  <th className="px-6 py-4 text-right">Beheer</th>
						  </tr>
					  </thead>
					  <tbody className="divide-y divide-brand-50">
						  {(chaletExtras || []).map((ex: any) => (
							  <tr key={ex.id}>
								  <td className="px-6 py-4">
									  <input type="text" value={ex.label} onChange={e => updateChaletExtra(ex.id, 'label', e.target.value)} className="w-full p-2 border rounded-lg text-sm"/>
								  </td>
								  <td className="px-6 py-4">
									  <input type="number" value={ex.price} onChange={e => updateChaletExtra(ex.id, 'price', Number(e.target.value))} className="w-full p-2 border rounded-lg text-sm"/>
								  </td>
								  <td className="px-6 py-4">
									  <select value={ex.priceType} onChange={e => updateChaletExtra(ex.id, 'priceType', e.target.value)} className="w-full p-2 border rounded-lg text-sm">
										  <option value="one-time">Eenmalig</option>
										  <option value="per-day">Per dag</option>
										  <option value="per-person">Per persoon</option>
									  </select>
								  </td>
								  <td className="px-6 py-4 text-right">
									  <button type="button" onClick={() => removeExtra(ex.id)} className="text-stone-300 hover:text-red-500 transition-colors p-2"><Trash2 size={16}/></button>
								  </td>
							  </tr>
						  ))}
						  {(chaletExtras || []).length === 0 && (
							  <tr>
								  <td colSpan={4} className="py-12 text-center text-stone-300 italic text-xs">Geen extra's geconfigureerd.</td>
							  </tr>
						  )}
					  </tbody>
				  </table>

                </div>

                {/* Day prices */}
                <div className="col-span-2 bg-brand-50 rounded-2xl p-6 border border-brand-100">
                  <h3 className="font-bold text-brand-900 mb-4 flex items-center gap-2">
                    <Coins size={18} /> Standaard Weekprijzen
                  </h3>
                  <p className="text-sm text-stone-500 mb-4">Stel hier de standaard prijs per dag van de week in.</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => (
                      <div key={day}>
                        <label className="block text-xs font-bold uppercase mb-1 text-stone-500">
                          {day === 'monday'
                            ? 'Ma'
                            : day === 'tuesday'
                            ? 'Di'
                            : day === 'wednesday'
                            ? 'Wo'
                            : day === 'thursday'
                            ? 'Do'
                            : day === 'friday'
                            ? 'Vr'
                            : day === 'saturday'
                            ? 'Za'
                            : 'Zo'}
                        </label>
                        <input type="number" value={dayPrices[day] ?? ''} onChange={(e) => updateDayPrice(day, Number(e.target.value))} className="w-full p-2 border rounded-lg text-sm" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom pricing */}
                <div className="col-span-2 bg-brand-50 rounded-2xl p-6 border border-brand-100">
                  <h3 className="font-bold text-brand-900 mb-4 flex items-center gap-2">
                    <Coins size={18} /> Seizoensprijzen & Afwijkende tarieven
                  </h3>
                  <p className="text-sm text-stone-500 mb-4">Prijzen hieronder overschrijven de standaard weekprijzen.</p>

                  <div className="flex flex-col md:flex-row gap-4 mb-4 items-end">
                    <div className="flex-grow">
                      <label className="block text-xs font-medium mb-1 text-stone-500">Van</label>
                      <input type="date" value={priceStart} onChange={(e) => setPriceStart(e.target.value)} className="p-2 border rounded-lg w-full" />
                    </div>
                    <div className="flex-grow">
                      <label className="block text-xs font-medium mb-1 text-stone-500">Tot</label>
                      <input type="date" value={priceEnd} onChange={(e) => setPriceEnd(e.target.value)} className="p-2 border rounded-lg w-full" />
                    </div>
                    <div className="w-32">
                      <label className="block text-xs font-medium mb-1 text-stone-500">Prijs/nacht</label>
                      <input type="number" value={customPriceVal} onChange={(e) => setCustomPriceVal(Number(e.target.value))} className="p-2 border rounded-lg w-full" />
                    </div>
                    <button type="button" onClick={handleAddCustomPrice} className="bg-brand-200 text-brand-800 px-4 py-2 rounded-lg font-medium hover:bg-brand-300 transition-colors">
                      Toevoegen
                    </button>
                  </div>

                  <div className="space-y-2">
                    {newCustomPrices.length === 0 && <p className="text-sm text-stone-400 italic">Geen afwijkende prijzen ingesteld.</p>}
                    {newCustomPrices.map((period, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-lg border border-brand-100 text-sm">
                        <span className="text-stone-600">
                          <span className="font-medium text-stone-800">{period.startDate}</span> tot{' '}
                          <span className="font-medium text-stone-800">{period.endDate}</span>: <span className="font-bold text-brand-800">€{period.price}</span> / nacht
                        </span>
                        <button type="button" onClick={() => handleRemoveCustomPrice(idx)} className="text-red-400 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Availability */}
                <div className="col-span-2 bg-brand-50 rounded-2xl p-6 border border-brand-100">
                  <h3 className="font-bold text-brand-900 mb-4 flex items-center gap-2">
                    <CalendarX size={18} /> Beschikbaarheid & Blokkades
                  </h3>

                  <div className="flex flex-col md:flex-row gap-4 mb-4 items-end">
                    <div className="flex-grow">
                      <label className="block text-xs font-medium mb-1 text-stone-500">Van</label>
                      <input type="date" value={blockStart} onChange={(e) => setBlockStart(e.target.value)} className="p-2 border rounded-lg w-full" />
                    </div>
                    <div className="flex-grow">
                      <label className="block text-xs font-medium mb-1 text-stone-500">Tot</label>
                      <input type="date" value={blockEnd} onChange={(e) => setBlockEnd(e.target.value)} className="p-2 border rounded-lg w-full" />
                    </div>
                    <button type="button" onClick={handleAddBlock} className="bg-brand-200 text-brand-800 px-4 py-2 rounded-lg font-medium hover:bg-brand-300 transition-colors">
                      Blokkeer
                    </button>
                  </div>

                  <div className="space-y-2">
                    {newUnavailablePeriods.length === 0 && <p className="text-sm text-stone-400 italic">Geen geblokkeerde periodes.</p>}
                    {newUnavailablePeriods.map((period, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-lg border border-brand-100 text-sm">
                        <span className="text-stone-600">
                          <span className="font-medium text-stone-800">{period.startDate}</span> tot <span className="font-medium text-stone-800">{period.endDate}</span>
                        </span>
                        <button type="button" onClick={() => handleRemoveBlock(idx)} className="text-red-400 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="col-span-2 flex justify-end gap-3 pt-4 border-t border-brand-100">
                  {/* Active toggle */}
                  <div className="flex-1 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setNewIsActive((v) => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        newIsActive ? 'bg-green-500' : 'bg-stone-300'
                      }`}
                      aria-checked={newIsActive}
                      role="switch"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          newIsActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm font-semibold text-stone-700">
                      {newIsActive ? 'Actief (zichtbaar op website)' : 'Inactief (verborgen voor gasten)'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsFormOpen(false);
                      resetForm();
                    }}
                    className="px-6 py-3 text-stone-500 font-medium hover:bg-stone-50 rounded-xl transition-colors"
                  >
                    Annuleren
                  </button>
                  <button type="submit" className="px-8 py-3 bg-brand-900 text-white rounded-xl font-bold shadow-md hover:bg-black transition-colors">
                    {isEditing ? 'Opslaan' : 'Toevoegen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          
          {/* Order actions */}
          <div className="flex items-center justify-between mb-4">
            {hasOrderChanges && (
              <button
                onClick={saveChaletOrder}
                disabled={isSavingOrder}
                className="px-4 py-2 rounded-xl font-bold shadow-sm border border-brand-200 bg-white hover:bg-brand-50 disabled:opacity-60"
              >
                {isSavingOrder ? "Opslaan..." : "Volgorde opslaan"}
              </button>
            )}
          </div>

{/* Table */}
          <div className="bg-white rounded-3xl shadow-sm border border-brand-100 overflow-hidden animate-in fade-in duration-500">

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-brand-100">
              {chalets.map((c) => {
                const count = countBookingsForChalet(c.id);
                return (
                  <div key={c.id} className="p-4 flex items-center gap-3">
                    <span
                      title="Sleep om te sorteren"
                      draggable
                      onDragStart={(e) => {
                        setDraggingChaletId(String(c.id));
                        e.dataTransfer.setData("text/plain", String(c.id));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setDraggingChaletId(null)}
                      className="cursor-grab active:cursor-grabbing text-stone-400 select-none text-lg shrink-0"
                    >≡</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-brand-900 truncate">{c.title}</p>
                      <p className="text-xs text-stone-500">{c.location}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                        <span>€{c.pricePerNight}/nacht</span>
                        <span>Schoonmaak €{c.cleaningFee}</span>
                        {count > 0 && (
                          <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 px-2 py-0.5 rounded-full font-bold">
                            <CalendarCheck size={11} /> {count}
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold ${
                          (c as any).isActive === 0 ? 'bg-stone-100 text-stone-500' : 'bg-green-100 text-green-700'
                        }`}>
                          {(c as any).isActive === 0 ? <EyeOff size={10} /> : <Eye size={10} />}
                          {(c as any).isActive === 0 ? 'Inactief' : 'Actief'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleEdit(c)} className="text-brand-600 hover:text-brand-800 p-2 rounded-full hover:bg-brand-50 transition-colors">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <table className="hidden md:table w-full text-left">
              <thead className="bg-brand-50 text-stone-600 font-medium">
                <tr>
                  <th className="p-6 w-10"></th>
                  <th className="p-6">Naam</th>
                  <th className="p-6">Locatie</th>
                  <th className="p-6">Prijs (vanaf)</th>
                  <th className="p-6">Schoonmaak</th>
                  <th className="p-6">Boekingen</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-100">
                {chalets.map((c) => {
                  const count = countBookingsForChalet(c.id);
                  return (
                    <tr key={c.id}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = draggingChaletId ?? e.dataTransfer.getData("text/plain");
                        if (from) moveChalet(from, String(c.id));
                        setDraggingChaletId(null);
                      }}
                      className="hover:bg-brand-50/30 transition-colors">
                      <td className="p-6 w-10">
                        <span
                          title="Sleep om te sorteren"
                          draggable
                          onDragStart={(e) => {
                            setDraggingChaletId(String(c.id));
                            e.dataTransfer.setData("text/plain", String(c.id));
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => setDraggingChaletId(null)}
                          className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-brand-900 select-none"
                        >≡</span>
                      </td>
                      <td className="p-6 font-medium text-brand-900">{c.title}</td>
                      <td className="p-6 text-stone-600">{c.location}</td>
                      <td className="p-6 text-stone-600">€{c.pricePerNight}</td>
                      <td className="p-6 text-stone-600">€{c.cleaningFee}</td>
                      <td className="p-6">
                        {count > 0 ? (
                          <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 px-2 py-1 rounded-full text-xs font-bold">
                            <CalendarCheck size={12} /> {count} actief
                          </span>
                        ) : (
                          <span className="text-stone-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="p-6">
                        <button
                          type="button"
                          title={(c as any).isActive !== 0 ? 'Klik om te deactiveren' : 'Klik om te activeren'}
                          onClick={async () => {
                            const newVal = (c as any).isActive === 0 ? 1 : 0;
                            await updateChalet({ ...(c as any), isActive: newVal });
                            refresh();
                          }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                            (c as any).isActive === 0
                              ? 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {(c as any).isActive === 0 ? <EyeOff size={11} /> : <Eye size={11} />}
                          {(c as any).isActive === 0 ? 'Inactief' : 'Actief'}
                        </button>
                      </td>
                      <td className="p-6 flex justify-end gap-2">
                        <button onClick={() => handleEdit(c)} className="text-brand-600 hover:text-brand-800 p-2 rounded-full hover:bg-brand-50 transition-colors" title="Bewerken">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors" title="Verwijderen">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

{/* Bookings Tab */}
{activeTab === "bookings" && (
  <div className="space-y-10 animate-in fade-in duration-500">
    {selectedBooking ? (
      /* CLEAN FUNCTIONAL DETAIL VIEW - UPDATED */
      <div className="bg-white rounded-xl border border-brand-100 shadow-md animate-in fade-in slide-in-from-right-4 duration-300 overflow-hidden">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-6 bg-brand-50 border-b border-brand-100 gap-4">
          <button
            onClick={() => setSelectedBooking(null)}
            className="flex items-center gap-2 text-brand-700 hover:text-brand-900 font-bold text-sm"
          >
            <ChevronLeft size={18} /> Terug naar lijst
          </button>

          {userRole !== 'cleaner' && (
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={`mailto:${(selectedBooking as any).guestEmail ?? ""}`}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-200 text-brand-900 rounded-lg text-xs font-bold hover:bg-brand-50 transition-colors"
              >
                <Send size={14} /> Contact opnemen
              </a>

              <button
                onClick={() => handleResendConfirmation(selectedBooking)}
                disabled={isProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-200 text-brand-900 rounded-lg text-xs font-bold hover:bg-brand-50 transition-colors"
              >
                {isProcessing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RotateCcw size={14} />
                )}
                {isProcessing ? "Verwerken..." : "Bevestiging opnieuw versturen"}
              </button>

              <button
                onClick={() => downloadBookingPDF(selectedBooking)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-200 text-brand-900 rounded-lg text-xs font-bold hover:bg-brand-50 transition-colors"
              >
                <Download size={14} /> Download PDF
              </button>

              <button
                onClick={() => handleCancelBooking(selectedBooking.id)}
                disabled={isProcessing || selectedBooking.status === "cancelled"}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg text-xs font-bold hover:bg-red-100 transition-colors"
              >
                <XCircle size={14} /> Boeking annuleren
              </button>
            </div>
          )}
        </div>

        {/* Data Grid */}
        <div className="p-8 animate-staggered-up" style={{ animationDelay: "0.1s" }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Column 1: Klantgegevens */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-stone-400 uppercase tracking-widest border-b border-brand-50 pb-2">
                Klantgegevens
              </h3>
              <div className="space-y-4">
                <div className="flex flex-col">
                  <span className="text-[10px] text-stone-400 uppercase font-bold">Naam</span>
                  <span className="text-brand-900 font-bold text-lg">
                    {(selectedBooking as any).guestName ?? "-"}
                  </span>
                </div>

                {userRole !== 'cleaner' && (
                  <div className="flex flex-col">
                    <span className="text-[10px] text-stone-400 uppercase font-bold">E-mail</span>
                    <a
                      href={`mailto:${(selectedBooking as any).guestEmail ?? ""}`}
                      className="text-brand-600 font-medium hover:underline"
                    >
                      {(selectedBooking as any).guestEmail ?? "-"}
                    </a>
                  </div>
                )}

                <div className="flex flex-col">
                  <span className="text-[10px] text-stone-400 uppercase font-bold">Telefoon</span>
                  <div className="flex items-center gap-2">
                    <span className="text-stone-700 font-medium">
                      {(selectedBooking as any).guestPhone ?? "-"}
                    </span>
                    {(selectedBooking as any).guestPhone && (
                      <button
                        onClick={() => {
                          const name = (selectedBooking as any).guestName ?? '';
                          const phone = (selectedBooking as any).guestPhone ?? '';
                          const code = (selectedBooking as any).confirmationCode ?? '';
                          const vcard = [
                            'BEGIN:VCARD',
                            'VERSION:3.0',
                            `FN:${name}`,
                            `TEL;TYPE=CELL:${phone}`,
                            `NOTE:Boeking ${code}`,
                            'END:VCARD',
                          ].join('\r\n');
                          const blob = new Blob([vcard], { type: 'text/vcard' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${name}.vcf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        title="Opslaan in contacten"
                        className="text-stone-400 hover:text-amber-600 transition-colors"
                      >
                        <UserPlus size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <span className="text-[10px] text-stone-400 uppercase font-bold block mb-1">Adres</span>
                  <p className="text-sm text-stone-600 leading-relaxed font-medium">
                    {(selectedBooking as any).guestAddress ?? "-"}
                    <br />
                    {(selectedBooking as any).guestZip ?? ""} {(selectedBooking as any).guestCity ?? ""}
                  </p>
                </div>
              </div>
            </div>

            {/* Column 2: Reservering */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-stone-400 uppercase tracking-widest border-b border-brand-50 pb-2">
                Reservering
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-stone-400 uppercase font-bold">Accommodatie</span>
                    <span className="text-brand-900 font-bold">
                      {chalets.find((c) => c.id === selectedBooking.chaletId)?.title || "Onbekend"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-stone-400 uppercase font-bold">Gasten</span>
                    <span className="text-brand-900 font-bold flex items-center gap-1">
                      <Users size={13} className="text-stone-400" />
                      {(selectedBooking as any).guests ?? "-"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-stone-400 uppercase font-bold">Check-in</span>
                    <span className="text-sm font-bold text-stone-800">
                      {format(parseISO(selectedBooking.startDate), "dd-MM-yyyy")}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-[10px] text-stone-400 uppercase font-bold">Check-out</span>
                    <span className="text-sm font-bold text-stone-800">
                      {format(parseISO(selectedBooking.endDate), "dd-MM-yyyy")}
                    </span>
                  </div>
                </div>

                <div className="pt-4">
                  <span className="text-[10px] text-stone-400 uppercase font-bold block mb-2">
                    Gekozen Extra&apos;s
                  </span>

				{selectedBooking.extras && selectedBooking.extras.length > 0 ? (
				  <ul className="space-y-1">
					{selectedBooking.extras.map((exId) => {
					  const opt = selectedChaletExtras.find((o) => o.id === exId);
					  return (
						<li key={exId} className="flex items-center gap-2 text-sm text-stone-700 font-medium">
						  <CheckCircle size={14} className="text-brand-500" />
						  {opt?.label || exId}
						</li>
					  );
					})}
				  </ul>
				) : (
				  <span className="text-xs text-stone-400 italic">Geen extra&apos;s geselecteerd</span>
				)}
                </div>

              </div>
            </div>

            {/* Column 3: Afrekening */}
            {userRole !== 'cleaner' && <div className="space-y-6">
              <h3 className="text-xs font-black text-stone-400 uppercase tracking-widest border-b border-brand-50 pb-2">
                Financieel
              </h3>

              <div className="bg-brand-50/50 p-6 rounded-2xl border border-brand-100 space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">Verblijf</span>
                  <span className="font-medium">
                    €
                    {(
                      selectedBooking.totalPrice -
                      (chalets.find((c) => c.id === selectedBooking.chaletId)?.cleaningFee || 0)
                    ).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">Schoonmaak</span>
                  <span className="font-medium">
                    €{chalets.find((c) => c.id === selectedBooking.chaletId)?.cleaningFee.toFixed(2) || "0.00"}
                  </span>
                </div>

                <div className="pt-4 border-t border-brand-100 flex justify-between items-center">
                  <span className="text-sm font-black text-brand-900 uppercase">Totaal</span>
                  <span className="text-3xl font-serif font-bold text-brand-900">
                    €{selectedBooking.totalPrice.toFixed(2)}
                  </span>
                </div>

                <div className="pt-2">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 w-fit ${badge.className}`}>
                    <StatusBadge status={selectedBooking.status} />
                  </span>
                </div>
              </div>
            </div>}
          </div>
        </div>

        {/* Extra Beddengoed — full-width prominent strip */}
        <div className={`border-t-2 px-8 py-5 flex items-center justify-between gap-6 transition-colors ${
          extraBeddengoed
            ? 'border-green-400 bg-green-50'
            : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${extraBeddengoed ? 'bg-green-100' : 'bg-red-100'}`}>
              <BedDouble size={20} className={extraBeddengoed ? 'text-green-700' : 'text-red-400'} />
            </div>
            <div>
              <p className="text-sm font-black text-stone-800 uppercase tracking-wide">Beddengoed</p>
              <p className={`text-xs mt-0.5 ${extraBeddengoed ? 'text-green-700' : 'text-red-400'}`}>
                {extraBeddengoed ? 'Gast heeft beddengoed nodig.' : 'Gast heeft geen beddengoed nodig.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleToggleBeddengoed(!extraBeddengoed)}
            disabled={beddengoedSaving || userRole === 'cleaner'}
            className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 shadow-inner ${
              extraBeddengoed ? 'bg-green-500' : 'bg-red-400'
            } ${userRole === 'cleaner' ? 'cursor-not-allowed' : ''}`}
            aria-label="Toggle extra beddengoed"
          >
            {beddengoedSaving
              ? <Loader2 size={12} className="absolute left-1/2 -translate-x-1/2 animate-spin text-white" />
              : <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                  extraBeddengoed ? 'translate-x-8' : 'translate-x-1'
                }`} />
            }
          </button>
        </div>

        {/* Internal Note Section */}
        <div className="border-t border-brand-100 bg-amber-50/40 px-8 py-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={15} className={bookingNote ? 'text-amber-500' : 'text-stone-400'} />
            <span className="text-xs font-black text-stone-400 uppercase tracking-widest">Notitie</span>
          </div>

          {noteLoading ? (
            <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
              <Loader2 size={14} className="animate-spin" /> Laden…
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                value={bookingNote}
                onChange={(e) => setBookingNote(e.target.value)}
                placeholder="✏️  Schrijf hier een notitie voor je collega's…"
                rows={3}
                className="w-full p-4 border border-amber-200 rounded-2xl text-sm text-brand-900 placeholder-stone-300 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white shadow-sm"
              />
              <div className="flex items-center justify-end gap-2">
                {bookingNote !== bookingNoteOriginal && (
                  <button
                    type="button"
                    onClick={() => setBookingNote(bookingNoteOriginal)}
                    className="px-3 py-1.5 text-xs font-semibold text-stone-500 hover:text-stone-700 rounded-xl border border-brand-100 bg-white hover:bg-brand-50"
                  >
                    Annuleren
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveNote}
                  disabled={noteSaving || bookingNote === bookingNoteOriginal}
                  className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {noteSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Notitie opslaan
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    ) : (
      <>
        {/* Top Controls */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          {/* Sub-Tab Navigation */}
          <div className="flex bg-white p-1 rounded-xl border border-brand-100 shadow-sm">
            <button
              onClick={() => { setBookingsSubTab("active"); setBookingsPage(1); }}
              className={`flex items-center gap-2 px-5 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                bookingsSubTab === "active"
                  ? "bg-brand-900 text-white shadow-md"
                  : "text-stone-400 hover:text-brand-900"
              }`}
            >
              <Inbox size={14} /> Actueel
            </button>

            <button
              onClick={() => { setBookingsSubTab("archive"); setBookingsPage(1); }}
              className={`flex items-center gap-2 px-5 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                bookingsSubTab === "archive"
                  ? "bg-brand-900 text-white shadow-md"
                  : "text-stone-400 hover:text-brand-900"
              }`}
            >
              <History size={14} /> Archief
            </button>
          </div>

          {/* Search & Status Filter */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {(() => {
              const uniqueApartmentIds = [...new Set(bookings.map(b => String(b.chaletId)))]
                .filter(id => {
                  const chalet = chalets.find(c => String(c.id) === id);
                  return (chalet as any)?.isActive !== 0;
                });
              if (uniqueApartmentIds.length > 1) {
                return (
                  <select
                    value={selectedApartmentFilter}
                    onChange={(e) => { setSelectedApartmentFilter(e.target.value); setBookingsPage(1); }}
                    className="px-4 py-2.5 bg-white border border-brand-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-stone-600 outline-none cursor-pointer shadow-sm focus:ring-1 focus:ring-brand-900 transition-all"
                  >
                    <option value="all">Alle appartementen</option>
                    {uniqueApartmentIds.map(id => {
                      const chalet = chalets.find(c => String(c.id) === id);
                      return (
                        <option key={id} value={id}>{chalet?.title ?? `Appartement ${id}`}</option>
                      );
                    })}
                  </select>
                );
              }
              return null;
            })()}

            <div className="relative flex-grow md:flex-grow-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                placeholder="Zoek gast..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setBookingsPage(1); }}
                className="pl-9 pr-4 py-2.5 bg-white border border-brand-100 rounded-xl text-[10px] font-black uppercase tracking-widest placeholder:normal-case placeholder:tracking-normal placeholder:font-normal placeholder:text-stone-400 text-stone-600 outline-none shadow-sm focus:ring-1 focus:ring-brand-900 transition-all w-full md:w-52"
              />
            </div>

            <select
              value={bookingStatusFilter}
              onChange={(e) => { setBookingStatusFilter(e.target.value as any); setBookingsPage(1); }}
              className="px-4 py-2.5 bg-white border border-brand-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-stone-600 outline-none cursor-pointer shadow-sm focus:ring-1 focus:ring-brand-900 transition-all"
            >
              <option value="all">Alle Statussen</option>
              <option value="confirmed">Bevestigd</option>
              <option value="pending">In afwachting</option>
              <option value="cancelled">Geannuleerd</option>
            </select>
          </div>
        </div>

        {/* LIST VIEW */}
        <div className="bg-white rounded-xl border border-brand-100 shadow-sm overflow-hidden animate-in fade-in duration-300">

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-brand-50">
            {pagedBookings.map((b) => (
              <div
                key={b.id}
                onClick={() => setSelectedBooking(b)}
                className="p-4 hover:bg-brand-50/30 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-bold text-brand-900 text-sm">{(b as any).guestName ?? "-"}</p>
                    <p className="text-xs text-stone-500">{chalets.find((c) => c.id === b.chaletId)?.title || "..."}</p>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-stone-400">
                    {format(parseISO(b.startDate.replace(" ", "T")), "dd MMM")} –{" "}
                    {format(parseISO(b.endDate.replace(" ", "T")), "dd MMM")}
                    <span className="ml-2">· {(b as any).guests ?? 1} gasten</span>
                  </p>
                  <div className="flex items-center gap-2">
                    {bookingNotesMap[b.id] && <MessageSquare size={13} className="text-amber-500" />}
                    <BedDouble size={13} className={b.extraBeddengoed ? "text-green-500" : "text-red-400"} />
                    {userRole !== 'cleaner' && (
                      <span className="font-bold text-brand-900 text-sm">€{b.totalPrice.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <table className="hidden md:table w-full text-left">
            <thead className="bg-brand-50 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
              <tr>
                <th className="p-5">Aangemaakt op</th>
                <th className="p-5">Gast</th>
                <th className="p-5">Verblijf</th>
                <th className="p-5">Gasten</th>
                <th className="p-5">Datum</th>
                {userRole !== 'cleaner' && <th className="p-5">Bedrag</th>}
                <th className="p-5">Status</th>
                <th className="p-5"><MessageSquare size={13} className="inline" /></th>
                <th className="p-5"><BedDouble size={13} className="inline" /></th>
                {userRole !== 'cleaner' && <th className="p-5">Acties</th>}
              </tr>
            </thead>

            <tbody className="divide-y divide-brand-50">
              {pagedBookings.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => setSelectedBooking(b)}
                  className="hover:bg-brand-50/30 cursor-pointer transition-colors group"
                >
                  <td className="p-5 text-sm font-medium text-stone-600">
                    {new Date(b.createdAt.replace(" ", "T")).toLocaleString("nl-NL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-brand-900">{(b as any).guestName ?? "-"}</span>
                      {userRole !== 'cleaner' && (
                        <span className="text-xs text-stone-400">{(b as any).guestEmail ?? ""}</span>
                      )}
                    </div>
                  </td>

                  <td className="p-5 text-sm font-medium text-stone-600">
                    {chalets.find((c) => c.id === b.chaletId)?.title || "..."}
                  </td>

                  <td className="p-5 text-sm font-medium text-stone-600">
                    {(b as any).guests ?? "-"}
                  </td>

                  <td className="p-5 text-sm text-stone-500">
                    {format(parseISO(b.startDate.replace(" ", "T")), "dd MMM")} -{" "}
                    {format(parseISO(b.endDate.replace(" ", "T")), "dd MMM")}
                  </td>

                  {userRole !== 'cleaner' && <td className="p-5 font-bold text-brand-900">€{b.totalPrice.toFixed(2)}</td>}

                  <td className="p-5 font-bold text-brand-900"><StatusBadge status={b.status} /></td>

                  <td className="p-5">
                    {bookingNotesMap[b.id] ? (
                      <span title="Heeft notitie">
                        <MessageSquare size={15} className="text-amber-500" />
                      </span>
                    ) : (
                      <span className="text-stone-200">
                        <MessageSquare size={15} />
                      </span>
                    )}
                  </td>

                  <td className="p-5">
                    {b.extraBeddengoed ? (
                      <span title="Extra beddengoed">
                        <BedDouble size={15} className="text-green-500" />
                      </span>
                    ) : (
                      <span title="Geen extra beddengoed">
                        <BedDouble size={15} className="text-red-400" />
                      </span>
                    )}
                  </td>

                  {userRole !== 'cleaner' && (
                    <td className="p-5 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadBookingPDF(b);
                        }}
                        className="p-2 text-stone-300 hover:text-brand-900"
                      >
                        <Download size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {visibleBookings.length === 0 && (
            <div className="p-20 text-center text-stone-300 font-medium">Geen boekingen gevonden</div>
          )}
        </div>

        {/* Pagination */}
        {totalBookingsPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <span className="text-xs text-stone-400">
              Pagina {bookingsPage} van {totalBookingsPages} &mdash; {visibleBookings.length} boekingen
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setBookingsPage(1)}
                disabled={bookingsPage === 1}
                className="px-3 py-1.5 rounded-xl border border-brand-100 text-xs font-semibold text-stone-600 hover:bg-brand-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >«</button>
              <button
                onClick={() => setBookingsPage((p) => Math.max(1, p - 1))}
                disabled={bookingsPage === 1}
                className="px-3 py-1.5 rounded-xl border border-brand-100 text-xs font-semibold text-stone-600 hover:bg-brand-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >‹</button>
              {Array.from({ length: totalBookingsPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalBookingsPages || Math.abs(p - bookingsPage) <= 1)
                .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-stone-400 text-xs">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setBookingsPage(p as number)}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors ${
                        bookingsPage === p
                          ? 'bg-brand-900 text-white border-brand-900'
                          : 'border-brand-100 text-stone-600 hover:bg-brand-50'
                      }`}
                    >{p}</button>
                  )
                )}
              <button
                onClick={() => setBookingsPage((p) => Math.min(totalBookingsPages, p + 1))}
                disabled={bookingsPage === totalBookingsPages}
                className="px-3 py-1.5 rounded-xl border border-brand-100 text-xs font-semibold text-stone-600 hover:bg-brand-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >›</button>
              <button
                onClick={() => setBookingsPage(totalBookingsPages)}
                disabled={bookingsPage === totalBookingsPages}
                className="px-3 py-1.5 rounded-xl border border-brand-100 text-xs font-semibold text-stone-600 hover:bg-brand-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >»</button>
            </div>
          </div>
        )}
      </>
    )}
  </div>
)}

      {activeTab === 'coupons' && !bookingsOnlyRole && (
          <div className="mx-auto space-y-10 animate-in fade-in duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Create Form */}
                  <div className="bg-white rounded-3xl border border-brand-100 rounded-sm shadow-xl p-8 h-fit">
                      <div className="flex items-center gap-3 mb-6">
                          <Tag className="text-brand-900" size={20} />
                          <h2 className="text-lg font-serif font-bold text-brand-900">Nieuwe Coupon</h2>
                      </div>
                      <form onSubmit={handleAddCoupon} className="space-y-5">
                          <div>
                              <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1.5">Code</label>
                              <div className="relative">
                                  <input 
                                      type="text" 
                                      value={newCoupon.code} 
                                      onChange={e => setNewCoupon(prev => ({ ...prev, code: e.target.value }))}
                                      className="w-full p-3 bg-brand-50 border border-brand-100 rounded-sm text-sm font-bold tracking-widest uppercase focus:ring-1 focus:ring-brand-900 outline-none" 
                                      placeholder="ZOMER2026"
                                  />
                                  <button type="button" onClick={generateRandomCode} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-300 hover:text-brand-900 transition-colors">
                                      <RefreshCw size={14} />
                                  </button>
                              </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1.5">Type</label>
                                  <select 
                                      value={newCoupon.discountType}
                                      onChange={e => setNewCoupon(prev => ({ ...prev, discountType: e.target.value as any }))}
                                      className="w-full p-3 bg-brand-50 border border-brand-100 rounded-sm text-sm"
                                  >
                                      <option value="percentage">Perc. (%)</option>
                                      <option value="fixed">Vast (€)</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1.5">Waarde</label>
                                  <input 
                                      type="number" 
                                      value={newCoupon.discountValue}
                                      onChange={e => setNewCoupon(prev => ({ ...prev, discountValue: Number(e.target.value) }))}
                                      className="w-full p-3 bg-brand-50 border border-brand-100 rounded-sm text-sm" 
                                      placeholder="0"
                                  />
                              </div>
                          </div>
                          <div>
                              <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1.5">Vervaldatum</label>
                              <div className="relative">
                                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-300" />
                                  <input 
                                      type="date" 
                                      value={newCoupon.expirationDate}
                                      onChange={e => setNewCoupon(prev => ({ ...prev, expirationDate: e.target.value }))}
                                      className="w-full pl-10 p-3 bg-brand-50 border border-brand-100 rounded-sm text-sm"
                                  />
                              </div>
                          </div>
                          <button type="submit" className="w-full py-3 bg-brand-900 text-white text-[10px] font-black uppercase tracking-widest rounded-sm hover:bg-black transition-all">Coupon Toevoegen</button>
                      </form>
                  </div>

                  {/* Coupon List */}
                  <div className="lg:col-span-2 bg-white rounded-3xl border border-brand-100 rounded-sm shadow-sm overflow-hidden">
                      <table className="w-full text-left">
                          <thead className="bg-brand-50 text-[9px] font-black uppercase tracking-widest border-b">
                              <tr>
                                  <th className="px-6 py-4">Code</th>
                                  <th className="px-6 py-4">Korting</th>
                                  <th className="px-6 py-4">Geldig tot</th>
                                  <th className="px-6 py-4">Status</th>
                                  <th className="px-6 py-4 text-right">Acties</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-brand-50">
							{coupons.map(c => {
							  const expired = checkIsExpired(c.expirationDate);
							  const isInactive = c.status === 'inactive';

							  return (
								<tr key={c.id} className="hover:bg-brand-50/30">
								  <td className="px-6 py-4 font-mono font-bold text-xs">
									{c.code}
								  </td>

								  <td className="px-6 py-4 text-xs font-medium">
									{c.discountType === 'percentage' ? `${c.discountValue}%` : `€${c.discountValue}`}
								  </td>

								  <td className="px-6 py-4 text-[10px] font-bold text-stone-500 uppercase">
									{c.expirationDate ? format(parseISO(c.expirationDate), 'dd-MM-yyyy') : '∞'}
								  </td>

									<td className="px-6 py-4">
									  {c.status === "inactive" ? (
										<span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-[8px] font-black uppercase border border-stone-200 rounded-sm">
										  Inactief
										</span>
									  ) : expired ? (
										<span className="px-2 py-0.5 bg-red-50 text-red-600 text-[8px] font-black uppercase border border-red-100 rounded-sm">
										  Verlopen
										</span>
									  ) : (
										<span className="px-2 py-0.5 bg-green-50 text-green-700 text-[8px] font-black uppercase border border-green-100 rounded-sm">
										  Actief
										</span>
									  )}
									</td>

								  <td className="px-6 py-4 text-right">
									{/* Optional: disable delete button if already inactive */}
									<button
									  onClick={() => handleDeleteCoupon(c.id)}
									  disabled={isInactive}
									  className={`p-2 transition-colors ${
										isInactive ? 'text-stone-200 cursor-not-allowed' : 'text-stone-300 hover:text-red-500'
									  }`}
									>
									  <Trash2 size={16} />
									</button>
								  </td>
								</tr>
							  );
							})}

                              {coupons.length === 0 && (
                                  <tr>
                                      <td colSpan={5} className="px-6 py-12 text-center text-stone-400 text-xs italic">Geen coupons gevonden.</td>
                                  </tr>
                              )}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      )}

      

      {activeTab === 'reviews' && !bookingsOnlyRole && (
        <div className="mx-auto space-y-6 animate-in fade-in duration-500">
          <div className="bg-white rounded-3xl border border-brand-100 shadow-xl p-6">
            <Reviews />
          </div>
        </div>
      )}

{activeTab === 'manage' && !bookingsOnlyRole && (
        <div className="mx-auto space-y-6 animate-in fade-in duration-500">
          <div className="bg-white rounded-3xl border border-brand-100 shadow-xl p-4 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Users className="text-brand-900" size={20} />
                <h2 className="text-lg font-serif font-bold text-brand-900">Gebruikers beheren</h2>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={refreshUsers}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-brand-100 bg-brand-50 hover:bg-brand-100 text-brand-900 text-sm font-semibold"
                >
                  <RefreshCw size={16} />
                  Vernieuwen
                </button>

                <button
                  type="button"
                  onClick={openCreateUser}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-brand-900 hover:bg-brand-800 text-white text-sm font-semibold shadow"
                >
                  <Plus size={16} />
                  Nieuwe gebruiker
                </button>
              </div>
            </div>

            {usersError && (
              <div className="mt-4 p-4 rounded-2xl border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5" />
                <div>{usersError}</div>
              </div>
            )}

            {usersSavedMsg && (
              <div className="mt-4 p-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 flex items-start gap-2">
                <CheckCircle size={16} className="mt-0.5" />
                <div>{usersSavedMsg}</div>
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-brand-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-50 text-stone-600">
                  <tr>
                    <th className="text-left p-3 whitespace-nowrap">Gebruiker</th>
                    <th className="text-left p-3 whitespace-nowrap">E-mail</th>
                    <th className="text-left p-3 whitespace-nowrap">Rol</th>
                    <th className="text-left p-3 whitespace-nowrap">Actief</th>
                    <th className="text-left p-3">Chalets</th>
                    <th className="text-right p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-100 bg-white">
                  {usersLoading ? (
                    <tr><td className="p-4 text-stone-500" colSpan={5}>Laden...</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td className="p-4 text-stone-500" colSpan={5}>Geen gebruikers gevonden.</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.id} className="hover:bg-brand-50/40">
                      <td className="p-3 font-semibold text-brand-900 whitespace-nowrap">{u.username}</td>
                      <td className="p-3 text-stone-500 text-sm whitespace-nowrap">{u.email ?? <span className="text-stone-300">—</span>}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-xl border border-brand-100 bg-brand-50 text-brand-900 text-xs font-semibold">
                          {u.role}
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {u.active ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={14} /> ja</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-stone-500"><XCircle size={14} /> nee</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-stone-700 min-w-0">
                        {u.role === "admin" ? "-" : (u.chaletIds?.length
                          ? u.chaletIds.map((id) => chalets.find((c) => Number(c.id) === Number(id))?.title ?? `#${id}`).join(", ")
                          : "-")}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEditUser(u)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-brand-100 bg-white hover:bg-brand-50 text-brand-900 text-xs font-semibold"
                        >
                          <Edit2 size={14} /> Bewerken
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {userModalOpen && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
              <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-brand-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-serif font-bold text-brand-900">
                    {editingUser ? `Gebruiker bewerken (ID ${editingUser.id})` : 'Nieuwe gebruiker'}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setUserModalOpen(false);
                      resetUserModal();
                    }}
                    className="p-2 rounded-2xl hover:bg-brand-50"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1">Gebruikersnaam</label>
                    <input
                      value={umUsername}
                      disabled={!!editingUser}
                      onChange={(e) => setUmUsername(e.target.value)}
                      className="w-full p-3 border rounded-2xl"
                      placeholder="bijv. admin"
                    />
                    {editingUser && (
                      <div className="text-[11px] text-stone-400 mt-1">Gebruikersnaam kan niet gewijzigd worden.</div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1">
                      {editingUser ? 'Nieuw wachtwoord (optioneel)' : 'Wachtwoord'}
                    </label>
                    <input
                      value={umPassword}
                      type="password"
                      onChange={(e) => setUmPassword(e.target.value)}
                      className="w-full p-3 border rounded-2xl"
                      placeholder={editingUser ? 'Laat leeg om niet te wijzigen' : '••••••••'}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1">E-mail <span className="font-normal text-stone-400">(optioneel)</span></label>
                    <input
                      value={umEmail}
                      type="email"
                      onChange={(e) => setUmEmail(e.target.value)}
                      className="w-full p-3 border rounded-2xl"
                      placeholder="bijv. eigenaar@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1">Rol</label>
                    <select value={umRole} onChange={(e) => setUmRole(e.target.value as any)} className="w-full p-3 border rounded-2xl">
                      <option value="admin">admin</option>
                      <option value="owner">owner</option>
                      <option value="cleaner">cleaner</option>
                    </select>
                  </div>

                  <div className="flex items-end gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-brand-900 font-semibold">
                      <input
                        type="checkbox"
                        checked={umActive}
                        onChange={(e) => setUmActive(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Actief
                    </label>
                  </div>

					<div className="md:col-span-2">
					  <label className="block text-xs font-semibold text-stone-600 mb-2">
						Chalet toegang
					  </label>

					  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-auto p-3 rounded-2xl border border-brand-100 bg-brand-50">
						{chalets.length === 0 ? (
						  <div className="text-sm text-stone-500">Geen chalets geladen.</div>
						) : (
						  chalets.map((c) => {
							const chaletIdNum = Number(c.id);
							const checked = umChaletIds.includes(chaletIdNum);

							return (
							  <label
								key={String(c.id)}
								className="flex items-center gap-2 text-sm text-brand-900"
							  >
								<input
								  type="checkbox"
								  checked={checked}
								  onChange={(e) => {
									setUmChaletIds((prev) => {
									  const set = new Set<number>(prev);
									  if (e.target.checked) set.add(chaletIdNum);
									  else set.delete(chaletIdNum);
									  return Array.from(set);
									});
								  }}
								  className="h-4 w-4"
								/>
								{/*<span className="text-stone-600 font-mono text-xs">{c.id}</span>*/}
								<span className="truncate">{c.title}</span>
							  </label>
							);
						  })
						)}
					  </div>
					</div>
					
                    <div className="text-[11px] text-stone-400 mt-1">
                      Voor owners en cleaners bepaalt dit welke chalets ze mogen zien/bewerken).
                    </div>
                  </div>


                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setUserModalOpen(false);
                      resetUserModal();
                    }}
                    className="px-4 py-2 rounded-2xl border border-brand-100 bg-white hover:bg-brand-50 text-brand-900 text-sm font-semibold"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={saveUser}
                    className="px-4 py-2 rounded-2xl bg-brand-900 hover:bg-brand-800 text-white text-sm font-semibold shadow"
                  >
                    <Save size={16} className="inline-block mr-2" />
                    Opslaan
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== BLOG TAB ===== */}
      {activeTab === 'blog' && isAdmin && (
        <div>
          {/* Blog post form */}
          {blogFormOpen && (
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-brand-100 mb-10 animate-in slide-in-from-top-4">
              <h2 className="text-xl font-bold text-brand-900 mb-6">
                {blogEditingPost ? `Bewerk "${blogEditingPost.title}"` : 'Nieuw blogbericht'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Titel *</label>
                  <input
                    value={blogTitle}
                    onChange={(e) => {
                      setBlogTitle(e.target.value);
                      if (!blogEditingPost) {
                        setBlogSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
                      }
                    }}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Bijv. De mooiste herfstbestemmingen"
                  />
                </div>

                {/* Slug */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">URL slug</label>
                  <input
                    value={blogSlug}
                    onChange={(e) => setBlogSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="w-full p-3 border border-brand-200 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="de-mooiste-herfstbestemmingen"
                  />
                  <p className="text-xs text-stone-400 mt-1">Wordt automatisch gegenereerd uit de titel.</p>
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Auteur</label>
                  <input
                    value={blogAuthor}
                    onChange={(e) => setBlogAuthor(e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Chalet Inn"
                  />
                </div>

                {/* Published toggle */}
                <div className="flex items-center gap-3 pt-6">
                  <button
                    type="button"
                    onClick={() => setBlogPublished(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      blogPublished ? 'bg-emerald-500' : 'bg-stone-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      blogPublished ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                    {blogPublished ? <><Eye size={15} className="text-emerald-600" /> Gepubliceerd</> : <><EyeOff size={15} className="text-stone-400" /> Concept</>}
                  </span>
                </div>

                {/* Excerpt */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Samenvatting / intro</label>
                  <textarea
                    value={blogExcerpt}
                    onChange={(e) => setBlogExcerpt(e.target.value)}
                    rows={2}
                    className="w-full p-3 border border-brand-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm"
                    placeholder="Korte beschrijving die zichtbaar is op de blogpagina…"
                  />
                </div>

                {/* Content */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-1">Inhoud *</label>
                  <textarea
                    value={blogContent}
                    onChange={(e) => setBlogContent(e.target.value)}
                    rows={14}
                    className="w-full p-3 border border-brand-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm font-mono leading-relaxed"
                    placeholder="Schrijf hier de inhoud van je bericht…&#10;&#10;HTML is ondersteund, bijv. <strong>vet</strong>, <em>cursief</em>, <br> voor regeleinde."
                  />
                  <p className="text-xs text-stone-400 mt-1">Ondersteunt HTML voor opmaak.</p>
                </div>

                {/* Cover image */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Omslagfoto</label>
                  <div className="flex flex-wrap items-center gap-3">
                    {blogCoverImage && (
                      <div className="relative w-32 h-20 rounded-xl overflow-hidden border border-brand-100 shadow-sm group">
                        <img src={blogCoverImage} alt="Cover" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setBlogCoverImage('')}
                          className="absolute top-1 right-1 p-1 bg-white/90 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl text-sm font-medium text-brand-800 cursor-pointer transition-colors">
                      {blogUploadingCover ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                      {blogCoverImage ? 'Vervangen' : 'Uploaden'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBlogImage(f, true); e.target.value = ''; }}
                      />
                    </label>
                    <span className="text-stone-400 text-xs">of</span>
                    <input
                      value={blogCoverImage}
                      onChange={(e) => setBlogCoverImage(e.target.value)}
                      className="flex-1 min-w-[180px] p-2.5 border border-brand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                      placeholder="https://... of /images/foto.jpg"
                    />
                  </div>
                </div>

                {/* Additional images */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Extra foto's</label>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {blogImages.map((img, idx) => (
                      <div key={idx} className="relative w-24 h-16 rounded-xl overflow-hidden border border-brand-100 shadow-sm group">
                        <img src={img} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setBlogImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 p-1 bg-white/90 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                    <label className="flex items-center gap-2 px-4 py-2 bg-brand-50 hover:bg-brand-100 border border-dashed border-brand-300 rounded-xl text-sm font-medium text-brand-700 cursor-pointer transition-colors h-16">
                      {blogUploadingImage ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                      Foto toevoegen
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBlogImage(f, false); e.target.value = ''; }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Save button */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-brand-100">
                <button
                  type="button"
                  onClick={() => { setBlogFormOpen(false); resetBlogForm(); }}
                  className="px-5 py-2.5 rounded-xl border border-brand-200 bg-white hover:bg-brand-50 text-brand-800 text-sm font-semibold transition-colors"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={handleBlogSave}
                  disabled={blogSaving || !blogTitle.trim() || !blogContent.trim()}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand-900 hover:bg-brand-800 text-white rounded-xl text-sm font-semibold shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {blogSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {blogEditingPost ? 'Wijzigingen opslaan' : 'Bericht aanmaken'}
                </button>
              </div>
            </div>
          )}

          {/* Blog posts list */}
          {!blogFormOpen && (
            <div className="bg-white rounded-3xl shadow-sm border border-brand-100 overflow-hidden">
              <div className="p-6 border-b border-brand-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-brand-900 text-lg">Blogberichten</h3>
                  <p className="text-stone-400 text-sm mt-0.5">{blogTotal} {blogTotal === 1 ? 'bericht' : 'berichten'} in totaal</p>
                </div>
                <a
                  href="#/blog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-800 font-medium transition-colors"
                >
                  <Globe size={14} /> Blog bekijken
                </a>
              </div>

              {blogLoading ? (
                <div className="flex items-center justify-center py-16 gap-3">
                  <Loader2 size={22} className="animate-spin text-brand-400" />
                  <span className="text-stone-500 text-sm">Laden…</span>
                </div>
              ) : blogPosts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-stone-400">
                  <BookOpen size={36} className="text-brand-200" />
                  <p className="text-sm">Nog geen berichten. Klik op "Nieuw bericht" om te beginnen.</p>
                </div>
              ) : (
                <div className="divide-y divide-brand-100">
                  {blogPosts.map((post) => (
                    <div key={post.id} className="flex items-center gap-4 px-6 py-4 hover:bg-brand-50/50 transition-colors">
                      {/* Cover thumbnail */}
                      <div className="w-14 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-brand-100">
                        {post.cover_image ? (
                          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileText size={16} className="text-brand-300" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-brand-900 text-sm truncate">{post.title}</span>
                          {post.published ? (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                              <Eye size={10} /> Gepubliceerd
                            </span>
                          ) : (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 text-[11px] font-semibold">
                              <EyeOff size={10} /> Concept
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-400 truncate">
                          Door {post.author} &nbsp;·&nbsp; {(() => { try { return new Date(post.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return post.created_at; } })()}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => openBlogEdit(post)}
                          className="p-2 rounded-xl hover:bg-brand-100 text-brand-600 hover:text-brand-800 transition-colors"
                          title="Bewerken"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => setBlogDeleteConfirm(post.id)}
                          className="p-2 rounded-xl hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          title="Verwijderen"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {blogPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-brand-100">
                  <p className="text-sm text-stone-400">Pagina {blogPage} van {blogPages}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadBlogPosts(blogPage - 1)}
                      disabled={blogPage === 1}
                      className="p-2 rounded-xl border border-brand-200 hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => loadBlogPosts(blogPage + 1)}
                      disabled={blogPage === blogPages}
                      className="p-2 rounded-xl border border-brand-200 hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Delete confirm modal */}
          {blogDeleteConfirm && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
              <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full border border-brand-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center">
                    <Trash2 size={20} className="text-red-500" />
                  </div>
                  <h3 className="font-bold text-brand-900 text-lg">Bericht verwijderen?</h3>
                </div>
                <p className="text-stone-500 text-sm mb-6">Dit kan niet ongedaan worden gemaakt.</p>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setBlogDeleteConfirm(null)}
                    className="px-4 py-2 rounded-xl border border-brand-200 bg-white hover:bg-brand-50 text-brand-800 text-sm font-semibold"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={() => handleBlogDelete(blogDeleteConfirm)}
                    className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold shadow"
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
   </>
  );
};
